import React, { useState, useEffect } from 'react';
import { CheckCircle, AlertTriangle, XCircle, Download, ArrowRight, Shield, FileText, BarChart3, ChevronDown, ChevronUp, TrendingUp, Award, Target, Zap, Clock, Briefcase, Filter, Plus, Check, Sun, Moon, Mail, BookOpen, Users, BarChart2, Star, Info } from 'lucide-react';
import { Button } from './ui/button';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, Legend } from 'recharts';
import { getAllQuickWins, getQuickWinsByPriority, getQuickWinsByTimeline, getQuickWinsByEffort } from '../data/quickWinsDatabase';
import { checkAchievements, calculateProgress, progressMilestones, calculateTotalPoints } from '../data/gamificationData';
import { getIndustryBenchmark, getTimelineEstimate, getBudgetEstimate } from '../data/benchmarkingData';
import { educationalContent } from '../data/educationalContent';
import EmailCaptureModal from './EmailCaptureModal';
import AchievementNotification from './AchievementNotification';
import { useTheme } from '../contexts/ThemeContext';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const ISO42001Tools = () => {
  const { isDarkMode, toggleTheme } = useTheme();
  const [selectedClause, setSelectedClause] = useState(null);
  const [auditResponses, setAuditResponses] = useState({});
  const [activeSection, setActiveSection] = useState('audit');
  const [showScoreSummary, setShowScoreSummary] = useState(true);
  const [actionPlan, setActionPlan] = useState([]);
  const [filterBy, setFilterBy] = useState('priority'); // priority, timeline, effort, clause
  
  // NEW: Gamification & Progress Tracking
  const [userAchievements, setUserAchievements] = useState([]);
  const [newAchievement, setNewAchievement] = useState(null);
  const [userEmail, setUserEmail] = useState('');
  const [showEmailModal, setShowEmailModal] = useState(false);
  
  // NEW: Benchmarking
  const [userIndustry, setUserIndustry] = useState('Technology');
  const [companySize, setCompanySize] = useState(100);
  const [showBenchmark, setShowBenchmark] = useState(false);
  
  // NEW: AI Recommendations
  const [aiRecommendations, setAiRecommendations] = useState(null);
  const [isGeneratingAI, setIsGeneratingAI] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [selectedFormat, setSelectedFormat] = useState('bullets'); // bullets, paragraphs, checklist, video
  
  // Load saved data from localStorage
  useEffect(() => {
    const savedResponses = localStorage.getItem('iso42001_responses');
    const savedActionPlan = localStorage.getItem('iso42001_action_plan');
    const savedEmail = localStorage.getItem('iso42001_user_email');
    const savedAchievements = localStorage.getItem('iso42001_achievements');
    const savedIndustry = localStorage.getItem('iso42001_industry');
    const savedCompanySize = localStorage.getItem('iso42001_company_size');
    
    if (savedResponses) {
      setAuditResponses(JSON.parse(savedResponses));
    }
    if (savedActionPlan) {
      setActionPlan(JSON.parse(savedActionPlan));
    }
    if (savedEmail) {
      setUserEmail(savedEmail);
    }
    if (savedAchievements) {
      setUserAchievements(JSON.parse(savedAchievements));
    }
    if (savedIndustry) {
      setUserIndustry(savedIndustry);
    }
    if (savedCompanySize) {
      setCompanySize(parseInt(savedCompanySize));
    }
  }, []);
  
  // Save to localStorage whenever responses or action plan change
  useEffect(() => {
    localStorage.setItem('iso42001_responses', JSON.stringify(auditResponses));
  }, [auditResponses]);
  
  useEffect(() => {
    localStorage.setItem('iso42001_action_plan', JSON.stringify(actionPlan));
  }, [actionPlan]);
  
  // NEW: Save email, achievements, industry, company size
  useEffect(() => {
    if (userEmail) {
      localStorage.setItem('iso42001_user_email', userEmail);
    }
  }, [userEmail]);
  
  useEffect(() => {
    localStorage.setItem('iso42001_achievements', JSON.stringify(userAchievements));
  }, [userAchievements]);
  
  useEffect(() => {
    localStorage.setItem('iso42001_industry', userIndustry);
  }, [userIndustry]);
  
  useEffect(() => {
    localStorage.setItem('iso42001_company_size', companySize.toString());
  }, [companySize]);
  
  // NEW: Show email modal after answering 5 questions if not saved
  useEffect(() => {
    const answeredCount = Object.keys(auditResponses).length;
    if (answeredCount === 5 && !userEmail) {
      setShowEmailModal(true);
    }
  }, [auditResponses, userEmail]);
  
  // NEW: Check for new achievements
  useEffect(() => {
    const totalQuestions = auditClauses.reduce((sum, clause) => sum + clause.questions.length, 0);
    const newAchievementsList = checkAchievements(auditResponses, auditClauses, actionPlan, userAchievements);
    
    if (newAchievementsList.length > userAchievements.length) {
      const latest = newAchievementsList[newAchievementsList.length - 1];
      setUserAchievements(newAchievementsList);
      setNewAchievement(latest);
    }
  }, [auditResponses, actionPlan]);

  // ISO 42001 Clauses for Deep Dive Audit - Complete Questions with Answers
  const auditClauses = [
    {
      id: 'clause4',
      title: 'Clause 4: Context of the Organization',
      questions: [
        {
          q: 'Have you determined external and internal issues relevant to your AI purpose?',
          a: 'The organization must identify and document internal and external factors that can affect its ability to achieve the intended results of its AI management system. This includes considering legal, technological, competitive, cultural, social, and economic environments (PESTLE analysis and SWOT for AI).'
        },
        {
          q: 'Do you track legal, regulatory, and contractual obligations related to AI?',
          a: 'The organization must identify, document, and keep up-to-date all legal, regulatory, and contractual requirements applicable to its AI systems. This requires a register of applicable laws (e.g., GDPR, AI Act), regulations, and contractual clauses.'
        },
        {
          q: 'Is the strategic direction of the organization aligned with the AI management system?',
          a: 'The organization\'s overall strategic direction must be considered when establishing the AIMS. The AIMS should support and contribute to achieving business goals, showing AI strategy is integrated into broader organizational strategy.'
        },
        {
          q: 'Have you identified all interested parties (stakeholders) relevant to the AI system?',
          a: 'Identify all individuals, groups, or organizations that have an interest in or are affected by the AI systems. This includes employees, customers, regulators, suppliers, and the public who can influence or be influenced by AI.'
        },
        {
          q: 'Are the requirements and expectations of these interested parties documented?',
          a: 'For each identified interested party, their relevant requirements and expectations concerning the AIMS and AI systems must be documented, including expectations for AI performance, safety, fairness, transparency, and privacy.'
        },
        {
          q: 'Do you have a process to update stakeholder requirements as they change?',
          a: 'Establish a mechanism to regularly review and update stakeholder requirements as circumstances, regulations, or expectations evolve, ensuring the AIMS remains relevant and responsive to the dynamic external environment.'
        },
        {
          q: 'Is the scope of the AI Management System formally defined and documented?',
          a: 'Clearly define the boundaries and applicability of the AIMS. The scope document should specify which AI systems, processes, products, services, and organizational units are covered.'
        },
        {
          q: 'Does the scope consider interfaces and dependencies with other systems?',
          a: 'The defined scope must acknowledge how AI systems and AIMS interact with and depend on other organizational systems (e.g., IT infrastructure, data management, operational processes) to ensure a holistic approach.'
        },
        {
          q: 'Have you established the processes needed for the AI management system?',
          a: 'Identify, establish, implement, maintain, and continually improve processes necessary for the AIMS. This involves creating process maps, flowcharts, or descriptions for all AI-related activities from development to management.'
        },
        {
          q: 'Do you continuously improve the AI management system processes?',
          a: 'The organization must continually improve the suitability, adequacy, and effectiveness of the AIMS using audit results, management reviews, corrective actions, and other feedback mechanisms - a core ISO principle.'
        }
      ]
    },
    {
      id: 'clause5',
      title: 'Clause 5: Leadership',
      questions: [
        {
          q: 'Does top management demonstrate leadership and commitment to the AIMS?',
          a: 'Top management must show visible commitment through establishing the AI policy, setting objectives, and providing necessary resources. Evidence includes participation in reviews, communication about AIMS, and budget allocation.'
        },
        {
          q: 'Are resources for the AIMS ensured by top management?',
          a: 'Top management must ensure necessary resources (human, financial, technological) are available to establish, implement, maintain, and improve the AIMS. This involves providing budgets, staffing, and infrastructure for AI governance.'
        },
        {
          q: "Is the AIMS integrated into the organization's business processes?",
          a: "The AIMS should be embedded within existing business processes and strategic planning, not standalone. This demonstrates that AI risk and management are integral to how the organization operates."
        },
        {
          q: 'Is there a documented AI Policy?',
          a: 'A formal AI Policy must be established, documented, and approved by top management. This policy should reflect the organization\'s commitment to responsible AI and sets the tone and direction for the AIMS.'
        },
        {
          q: 'Does the policy include a commitment to satisfy applicable requirements?',
          a: 'The AI Policy must explicitly state the organization\'s commitment to comply with all applicable legal, regulatory, and contractual obligations related to AI, as well as stakeholder requirements - a mandatory policy element.'
        },
        {
          q: 'Is the policy available to interested parties?',
          a: 'The AI Policy should be communicated within the organization and made available to relevant external interested parties as appropriate, promoting transparency and awareness of responsible AI commitment.'
        },
        {
          q: 'Are roles and responsibilities for the AIMS assigned?',
          a: 'Top management must assign roles, responsibilities, and authorities for the AIMS to ensure effective operation and maintenance. Clear assignment prevents ambiguity and ensures accountability.'
        },
        {
          q: 'Is there a specific role for reporting on AIMS performance?',
          a: 'Consider assigning responsibility for reporting on AIMS performance and effectiveness to top management. This could be a dedicated role or part of existing management responsibilities.'
        },
        {
          q: 'Are authorities for AI decision-making clearly defined?',
          a: 'Clearly define who has authority to make decisions related to AI systems, including development, deployment, modification, and decommissioning. This is crucial for accountability and preventing unauthorized AI actions.'
        },
        {
          q: 'Are these roles communicated within the organization?',
          a: 'The assigned roles, responsibilities, and authorities for the AIMS must be effectively communicated throughout the organization to ensure everyone understands their part in the AIMS.'
        }
      ]
    },
    {
      id: 'clause6',
      title: 'Clause 6: Planning',
      questions: [
        {
          q: 'Have you planned actions to address risks and opportunities?',
          a: 'Plan actions to address risks and opportunities related to the AIMS and AI systems by considering internal/external issues and stakeholder requirements. This is about proactive planning to prevent negatives and capitalize on positives.'
        },
        {
          q: 'Do you have a documented AI risk assessment process?',
          a: 'A formal, documented process for identifying, analyzing, and evaluating AI risks must be in place and applied throughout the AI system lifecycle. This is a cornerstone of the AIMS for managing AI-specific risks like bias, safety, and privacy.'
        },
        {
          q: 'Are risk criteria established and consistent?',
          a: 'Define clear criteria for determining AI risk significance, including acceptable risk levels. These criteria should be applied consistently to ensure risks are evaluated objectively and comparably.'
        },
        {
          q: 'Are AI risks identified throughout the system lifecycle?',
          a: 'Risk assessment should occur at all stages from conception and design to development, deployment, operation, and decommissioning - not just one-time. This ensures emerging risks at different stages are identified.'
        },
        {
          q: 'Are risk owners identified for each risk?',
          a: 'For each identified AI risk, assign a specific individual or role (risk owner) responsible for its management and oversight. This ensures accountability for managing each identified risk.'
        },
        {
          q: 'Do you have a risk treatment plan?',
          a: 'Based on risk assessment, develop a plan to treat identified AI risks, outlining selected risk treatment options and required actions. The plan details how risks will be managed, reduced, transferred, or accepted.'
        },
        {
          q: 'Have you produced a Statement of Applicability (SoA)?',
          a: 'A Statement of Applicability (SoA) should detail which ISO 42001 controls are applicable and why, and whether they are implemented. The SoA demonstrates how the organization addresses the standard\'s requirements.'
        },
        {
          q: 'Are residual risks approved by risk owners?',
          a: 'Any residual risks (remaining after treatment) must be reviewed and formally approved by designated risk owners or appropriate management, confirming awareness and acceptance of remaining risks.'
        },
        {
          q: 'Are AI objectives established at relevant functions?',
          a: 'Establish measurable AI objectives that are relevant to functions and levels within the organization and support the AI policy and strategic direction, aligned with overall AI strategy and business goals.'
        },
        {
          q: 'Are the objectives measurable and monitored?',
          a: 'Ensure AI objectives are measurable, monitor their progress, and communicate results. Determine what needs to be done, resources required, who is responsible, and completion timeline to track progress and demonstrate AIMS effectiveness.'
        }
      ]
    },
    {
      id: 'clause7',
      title: 'Clause 7: Support',
      questions: [
        {
          q: 'Are adequate resources (people, infrastructure) determined and provided?',
          a: 'Determine and provide necessary resources (human, infrastructure, technology, financial) for the AIMS. This includes skilled personnel, appropriate IT infrastructure, and necessary software/hardware.'
        },
        {
          q: 'Is data quality and availability sufficient for the AI system?',
          a: 'Ensure data used to train, operate, and validate AI systems is of sufficient quality, quantity, and availability to meet system requirements and AI policy commitments. This includes data governance, integrity, and accessibility.'
        },
        {
          q: 'Is the competence of persons affecting AIMS performance determined?',
          a: 'Determine necessary competence for personnel whose work affects AIMS performance and AI systems. This applies to individuals involved in design, development, deployment, operation, and oversight of AI.'
        },
        {
          q: 'Are training or other actions taken to acquire necessary competence?',
          a: 'Ensure personnel have required competence by providing appropriate training or taking other actions. Retain documented information of competence as evidence through formal training, on-the-job learning, or hiring qualified individuals.'
        },
        {
          q: 'Are persons aware of the AI policy and their contribution?',
          a: 'Ensure all persons working under the organization\'s control are aware of the AI policy, relevant AI objectives, and their individual contribution to AIMS effectiveness to understand their role in achieving responsible AI.'
        },
        {
          q: 'Are persons aware of the implications of not conforming to AIMS requirements?',
          a: 'Personnel should understand the consequences of not conforming to AIMS requirements, reinforcing the importance of following established procedures and policies.'
        },
        {
          q: 'Is there a defined process for internal and external communication?',
          a: 'Establish documented processes for internal and external communication relevant to the AIMS, including what to communicate, when, to whom, and how. Effective communication is vital for managing AI risks and fostering collaboration.'
        },
        {
          q: 'Are communication protocols for AI incidents established?',
          a: 'Specific communication protocols for AI-related incidents (e.g., security breaches, significant performance issues, unexpected bias) should be established to ensure swift and coordinated response to AI problems.'
        },
        {
          q: 'Is documented information required by ISO 42001 controlled?',
          a: 'Implement processes to establish, update, control, and retain documented information required by the standard, ensuring it is identifiable, retrievable, and protected. This covers all AIMS records and documents.'
        },
        {
          q: 'Are external documents necessary for the AIMS identified and controlled?',
          a: 'Identify and control any external documents necessary for effective AIMS operation, such as standards, regulations, or supplier documentation, ensuring the organization uses up-to-date and relevant external information.'
        }
      ]
    },
    {
      id: 'clause8',
      title: 'Clause 8: Operation',
      questions: [
        {
          q: 'Do you plan, implement, and control processes to meet requirements?',
          a: 'Plan, implement, and control processes needed to meet requirements for AI systems and services provision. This includes operational planning and control to ensure day-to-day AI operations are managed effectively.'
        },
        {
          q: 'Are criteria for the processes established?',
          a: 'Establish criteria for processes and define criteria for acceptance of AI systems and services. Clear criteria ensure consistent quality and performance of AI systems.'
        },
        {
          q: 'Do you perform AI risk assessments at planned intervals?',
          a: 'Conduct AI risk assessments at planned intervals and whenever significant changes occur in the AI system or operating environment. This is a continuous process, not a one-off activity.'
        },
        {
          q: 'Do assessments account for changes in the AI system or context?',
          a: 'Ensure AI risk assessments consider all relevant changes to the AI system (e.g., updates, new data) and its operational context to keep identified risks relevant and capture new risks.'
        },
        {
          q: 'Are the results of risk assessments documented?',
          a: 'Maintain documented information of AI risk assessment results, including identified risks, their evaluation, and decisions made regarding them, providing a record of the process and outcomes.'
        },
        {
          q: 'Do you implement the risk treatment plan effectively?',
          a: 'Implement planned risk treatment actions effectively to address identified AI risks. This is where planning from Clause 6 is put into action.'
        },
        {
          q: 'Are risk treatments verified for effectiveness?',
          a: 'Verify that implemented risk treatments are effective in reducing identified risks to an acceptable level. Effectiveness verification ensures treatments achieve their intended purpose.'
        },
        {
          q: 'Do you retain documented information of risk treatment results?',
          a: 'Retain documented information of risk treatment implementation and verification results, providing evidence of actions taken and their outcomes.'
        },
        {
          q: 'Do you assess the impact of AI systems on individuals and society?',
          a: 'Conduct assessments to understand and evaluate potential AI system impact on individuals (e.g., fairness, privacy, autonomy) and society (e.g., employment, public trust), addressing ethical and societal implications.'
        },
        {
          q: 'Are third-party suppliers of AI systems controlled?',
          a: 'Ensure third-party suppliers providing AI systems or services are evaluated and controlled so their AI systems meet the organization\'s AI policy and requirements, extending AIMS to external parties.'
        }
      ]
    },
    {
      id: 'clause9',
      title: 'Clause 9: Performance Evaluation',
      questions: [
        {
          q: 'Do you determine what needs to be monitored and measured?',
          a: 'Determine what needs to be monitored and measured regarding the AIMS and AI system performance. This includes identifying key performance indicators (KPIs) that demonstrate AIMS and AI system effectiveness and efficiency.'
        },
        {
          q: 'Are methods for monitoring and analysis defined to ensure valid results?',
          a: 'Define methods for monitoring, analysis, and evaluation to ensure results are valid and repeatable. This includes calibration and validation of measurement equipment/processes for reliable monitoring data.'
        },
        {
          q: 'Do you evaluate the performance and effectiveness of the AIMS?',
          a: 'Periodically evaluate the AIMS performance and effectiveness and its processes. This helps identify areas for improvement and confirm the AIMS is achieving its intended goals.'
        },
        {
          q: 'Is documented information retained as evidence of results?',
          a: 'Retain documented information as evidence of monitoring, measurement, analysis, and evaluation results. These records serve as proof of performance evaluation activities.'
        },
        {
          q: 'Do you conduct internal audits at planned intervals?',
          a: 'Conduct internal audits at planned intervals to provide information on whether the AIMS conforms to requirements and ISO 42001, and whether it\'s effectively implemented. Internal audits are key for self-assessment.'
        },
        {
          q: 'Are auditors objective and impartial?',
          a: 'Ensure internal auditors are objective and impartial. The audit program should define auditor selection criteria. Objectivity and impartiality are critical for unbiased audit findings.'
        },
        {
          q: 'Are audit results reported to relevant management?',
          a: 'Internal audit results must be reported to relevant management, who will take appropriate action to address nonconformities and opportunities for improvement. Findings must be communicated to enable corrective actions.'
        },
        {
          q: 'Does top management review the AIMS at planned intervals?',
          a: 'Top management must review the AIMS at planned intervals to ensure its continuing suitability, adequacy, and effectiveness. Management review is crucial input for strategic decision-making and continual improvement.'
        },
        {
          q: 'Does the management review include inputs like changes, performance, and risks?',
          a: 'Management review inputs must include relevant information such as changes in external/internal issues, feedback from interested parties, AIMS performance, audit results, corrective action status, and risks for thorough coverage.'
        },
        {
          q: 'Are decisions related to continual improvement output from the review?',
          a: 'Management review outputs must include decisions and actions related to opportunities for continual AIMS improvement and any required changes. The review is expected to lead to concrete improvement actions.'
        }
      ]
    },
    {
      id: 'clause10',
      title: 'Clause 10: Improvement',
      questions: [
        {
          q: 'Do you react to nonconformities and take action to control/correct them?',
          a: 'When a nonconformity occurs, react to it and take action to control and correct it, including addressing immediate consequences. This is the first step in managing nonconformities.'
        },
        {
          q: 'Do you evaluate the need for action to eliminate root causes?',
          a: 'Evaluate the need to eliminate the cause(s) of the nonconformity to prevent recurrence through root cause analysis. Identifying the root cause is essential for effective corrective actions.'
        },
        {
          q: 'Do you implement any action needed effectively?',
          a: 'Implement any action needed to eliminate the root cause(s) effectively. This is the execution phase of corrective actions to address identified problems.'
        },
        {
          q: 'Do you review the effectiveness of corrective actions?',
          a: 'Review the effectiveness of corrective action taken to ensure it has addressed the root cause and prevented recurrence. This verification step is critical to confirm corrective action success.'
        },
        {
          q: 'Do you make changes to the AIMS if necessary after nonconformities?',
          a: 'If nonconformities indicate AIMS deficiencies, make necessary changes to prevent future occurrences. This links nonconformities back to improving the system itself.'
        },
        {
          q: 'Do you continually improve the suitability, adequacy, and effectiveness of the AIMS?',
          a: 'Continually improve the AIMS suitability, adequacy, and effectiveness in line with the organization\'s AI policy and objectives. This is the overarching goal of the improvement clause.'
        },
        {
          q: 'Do you use outputs of analysis and evaluation to identify needs for improvement?',
          a: 'Utilize results of analysis and evaluation (e.g., from performance monitoring, audits, management reviews) to identify improvement opportunities, ensuring improvements are data-driven and based on actual performance.'
        },
        {
          q: 'Are improvement opportunities prioritized?',
          a: 'Prioritize improvement opportunities based on their potential impact, feasibility, and alignment with strategic goals to ensure resources are focused on the most impactful improvements.'
        },
        {
          q: 'Do you use innovation to improve AI performance?',
          a: 'Actively seek and implement innovative approaches to enhance AI system performance, fairness, transparency, and safety. Innovation is encouraged to drive advancement in responsible AI practices.'
        },
        {
          q: 'Is there evidence of improvement over time?',
          a: 'Demonstrate evidence of continual improvement in the AIMS and AI systems over time, supported by relevant metrics and records. This provides objective proof that the AIMS is effective in driving positive change.'
        }
      ]
    }
  ];

  // Risk categories for heatmap
  const riskCategories = [
    { id: 'bias', name: 'Bias & Discrimination', severity: 'high', likelihood: 'high' },
    { id: 'privacy', name: 'Privacy Violations', severity: 'critical', likelihood: 'medium' },
    { id: 'security', name: 'Security Breaches', severity: 'critical', likelihood: 'medium' },
    { id: 'transparency', name: 'Lack of Transparency', severity: 'medium', likelihood: 'high' },
    { id: 'accuracy', name: 'Model Accuracy', severity: 'high', likelihood: 'medium' },
    { id: 'accountability', name: 'Accountability Gaps', severity: 'medium', likelihood: 'medium' },
    { id: 'compliance', name: 'Regulatory Non-compliance', severity: 'critical', likelihood: 'low' },
    { id: 'ethical', name: 'Ethical Concerns', severity: 'high', likelihood: 'medium' }
  ];

  // Implementation phases
  const implementationPhases = [
    {
      phase: 'Discovery & Scope',
      status: 'completed',
      description: 'Define AI system scope and boundaries',
      duration: '2-4 weeks'
    },
    {
      phase: 'Risk Assessment',
      status: 'in-progress',
      description: 'Conduct comprehensive AI risk analysis',
      duration: '3-6 weeks'
    },
    {
      phase: 'Controls Implementation',
      status: 'pending',
      description: 'Deploy technical and organizational controls',
      duration: '8-12 weeks'
    },
    {
      phase: 'Internal Audit',
      status: 'pending',
      description: 'Verify ISO 42001 compliance',
      duration: '2-3 weeks'
    },
    {
      phase: 'Certification Support',
      status: 'pending',
      description: 'Prepare for external certification audit',
      duration: '4-6 weeks'
    }
  ];

  const handleAuditResponse = (clauseId, questionIndex, response) => {
    setAuditResponses(prev => ({
      ...prev,
      [`${clauseId}-${questionIndex}`]: response
    }));
  };

  // Calculate overall audit score (weighted)
  const calculateAuditScore = () => {
    const totalQuestions = auditClauses.reduce((sum, clause) => sum + clause.questions.length, 0);
    const responses = Object.values(auditResponses);
    const yesCount = responses.filter(r => r === 'yes').length;
    const partialCount = responses.filter(r => r === 'partial').length;
    
    // Weighted: Yes = 100%, Partial = 50%, No = 0%
    const totalScore = (yesCount * 100) + (partialCount * 50);
    const maxScore = totalQuestions * 100;
    
    return totalQuestions > 0 ? Math.round((totalScore / maxScore) * 100) : 0;
  };

  // Calculate score for individual clause
  const calculateClauseScore = (clauseId) => {
    const clause = auditClauses.find(c => c.id === clauseId);
    if (!clause) return { score: 0, answered: 0, total: 0, yes: 0, partial: 0, no: 0 };
    
    const totalQuestions = clause.questions.length;
    let yesCount = 0, partialCount = 0, noCount = 0, answeredCount = 0;
    
    clause.questions.forEach((_, index) => {
      const response = auditResponses[`${clauseId}-${index}`];
      if (response) {
        answeredCount++;
        if (response === 'yes') yesCount++;
        else if (response === 'partial') partialCount++;
        else if (response === 'no') noCount++;
      }
    });
    
    const totalScore = (yesCount * 100) + (partialCount * 50);
    const maxScore = totalQuestions * 100;
    const score = totalQuestions > 0 ? Math.round((totalScore / maxScore) * 100) : 0;
    
    return { score, answered: answeredCount, total: totalQuestions, yes: yesCount, partial: partialCount, no: noCount };
  };

  // Get overall statistics
  const getOverallStats = () => {
    const totalQuestions = auditClauses.reduce((sum, clause) => sum + clause.questions.length, 0);
    const responses = Object.values(auditResponses);
    const yesCount = responses.filter(r => r === 'yes').length;
    const partialCount = responses.filter(r => r === 'partial').length;
    const noCount = responses.filter(r => r === 'no').length;
    const answeredCount = responses.length;
    const unansweredCount = totalQuestions - answeredCount;
    
    return {
      total: totalQuestions,
      answered: answeredCount,
      unanswered: unansweredCount,
      yes: yesCount,
      partial: partialCount,
      no: noCount
    };
  };

  // Get radar chart data
  const getRadarChartData = () => {
    return auditClauses.map(clause => {
      const clauseScore = calculateClauseScore(clause.id);
      return {
        clause: clause.title.replace('Clause ', 'C').split(':')[0], // Shorten for chart
        score: clauseScore.score,
        fullMark: 100
      };
    });
  };

  // Get recommendations based on scores
  const getRecommendations = () => {
    const recommendations = [];
    const clauseScores = auditClauses.map(clause => ({
      ...clause,
      ...calculateClauseScore(clause.id)
    }));
    
    // Find lowest scoring clauses
    const lowestScores = clauseScores
      .filter(c => c.answered > 0)
      .sort((a, b) => a.score - b.score)
      .slice(0, 2);
    
    lowestScores.forEach(clause => {
      if (clause.score < 50) {
        recommendations.push({
          type: 'critical',
          title: clause.title,
          message: `Critical attention needed. Only ${clause.score}% compliant. Focus on establishing fundamental requirements.`
        });
      } else if (clause.score < 75) {
        recommendations.push({
          type: 'warning',
          title: clause.title,
          message: `Needs improvement. Current score: ${clause.score}%. Address gaps in documentation and implementation.`
        });
      }
    });
    
    // Check for unanswered questions
    const stats = getOverallStats();
    if (stats.unanswered > 20) {
      recommendations.push({
        type: 'info',
        title: 'Assessment Incomplete',
        message: `${stats.unanswered} questions remain unanswered. Complete the assessment for accurate recommendations.`
      });
    }
    
    // Add positive feedback for high scores
    const highScores = clauseScores
      .filter(c => c.score >= 75 && c.answered > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 2);
    
    if (highScores.length > 0) {
      recommendations.push({
        type: 'success',
        title: 'Strong Performers',
        message: `Excellent compliance in ${highScores.map(c => c.title.split(':')[0]).join(' and ')}. Continue maintaining these standards.`
      });
    }
    
    return recommendations;
  };

  // Get score color
  const getScoreColor = (score) => {
    if (score >= 75) return 'text-green-400 bg-green-500/20 border-green-500/50';
    if (score >= 50) return 'text-yellow-400 bg-yellow-500/20 border-yellow-500/50';
    return 'text-red-400 bg-red-500/20 border-red-500/50';
  };

  // Get score label
  const getScoreLabel = (score) => {
    if (score >= 90) return 'Excellent';
    if (score >= 75) return 'Good';
    if (score >= 50) return 'Needs Improvement';
    return 'Critical';
  };
  
  // Quick Wins functions
  const getAvailableQuickWins = () => {
    return getAllQuickWins(auditResponses);
  };
  
  const getQuickWinsCount = () => {
    return getAvailableQuickWins().length;
  };
  
  const addToActionPlan = (quickWin) => {
    if (!actionPlan.find(item => item.id === quickWin.id)) {
      setActionPlan([...actionPlan, { ...quickWin, completed: false, addedAt: new Date().toISOString() }]);
    }
  };
  
  const removeFromActionPlan = (quickWinId) => {
    setActionPlan(actionPlan.filter(item => item.id !== quickWinId));
  };
  
  const toggleQuickWinComplete = (quickWinId) => {
    setActionPlan(actionPlan.map(item => 
      item.id === quickWinId ? { ...item, completed: !item.completed } : item
    ));
  };
  
  const isInActionPlan = (quickWinId) => {
    return actionPlan.some(item => item.id === quickWinId);
  };
  
  const getFilteredQuickWins = () => {
    const allQuickWins = getAvailableQuickWins();
    
    switch(filterBy) {
      case 'priority':
        const byPriority = getQuickWinsByPriority(allQuickWins);
        return [
          ...byPriority.critical.map(qw => ({ ...qw, group: 'Critical Priority' })),
          ...byPriority.high.map(qw => ({ ...qw, group: 'High Priority' })),
          ...byPriority.medium.map(qw => ({ ...qw, group: 'Medium Priority' }))
        ];
      case 'timeline':
        const byTimeline = getQuickWinsByTimeline(allQuickWins);
        return [
          ...byTimeline.immediate.map(qw => ({ ...qw, group: 'Immediate (1-3 days)' })),
          ...byTimeline.short.map(qw => ({ ...qw, group: 'Short-term (1-2 weeks)' })),
          ...byTimeline.medium.map(qw => ({ ...qw, group: 'Medium-term (1 month)' }))
        ];
      case 'effort':
        const byEffort = getQuickWinsByEffort(allQuickWins);
        return [
          ...byEffort.low.map(qw => ({ ...qw, group: 'Low Effort' })),
          ...byEffort.medium.map(qw => ({ ...qw, group: 'Medium Effort' })),
          ...byEffort.high.map(qw => ({ ...qw, group: 'High Effort' }))
        ];
      case 'clause':
        const byClause = {};
        allQuickWins.forEach(qw => {
          const clauseTitle = auditClauses.find(c => c.id === qw.clauseId)?.title || 'Unknown';
          if (!byClause[clauseTitle]) byClause[clauseTitle] = [];
          byClause[clauseTitle].push(qw);
        });
        return Object.entries(byClause).flatMap(([title, wins]) => 
          wins.map(qw => ({ ...qw, group: title }))
        );
      default:
        return allQuickWins.map(qw => ({ ...qw, group: 'All Quick Wins' }));
    }
  };
  
  const downloadTemplate = (templateName) => {
    // In a real implementation, this would download actual templates
    alert(`Downloading ${templateName}... (Templates would be provided in production)`);
  };
  
  const exportActionPlan = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPos = 20;

    // Helper function to add new page if needed
    const checkPageBreak = (requiredSpace) => {
      if (yPos + requiredSpace > pageHeight - 20) {
        doc.addPage();
        yPos = 20;
        return true;
      }
      return false;
    };

    // Add logo
    const logo = new Image();
    logo.src = '/shanso-logo.png';
    doc.addImage(logo, 'PNG', 14, 8, 40, 15); // x, y, width, height

    // Header
    doc.setFillColor(20, 184, 166); // Cyan color
    doc.rect(0, 0, pageWidth, 30, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(20);
    doc.text('ISO 42001 Action Plan', pageWidth / 2, 18, { align: 'center' });
    
    yPos = 40;

    // Export Date
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, yPos);
    yPos += 10;

    // Overall Summary
    doc.setFontSize(16);
    doc.setTextColor(20, 184, 166);
    doc.text('Assessment Summary', 14, yPos);
    yPos += 10;

    const stats = getOverallStats();
    const overallScore = calculateAuditScore();
    
    autoTable(doc, {
      startY: yPos,
      head: [['Metric', 'Value']],
      body: [
        ['Overall Compliance Score', `${overallScore}% - ${getScoreLabel(overallScore)}`],
        ['Total Questions', stats.total],
        ['Answered', stats.answered],
        ['Fully Compliant (Yes)', stats.yes],
        ['Partially Compliant', stats.partial],
        ['Non-Compliant (No)', stats.no],
        ['Unanswered', stats.unanswered],
        ['Total Action Items', actionPlan.length],
        ['Completed Actions', actionPlan.filter(item => item.completed).length]
      ],
      theme: 'grid',
      headStyles: { fillColor: [20, 184, 166] },
      margin: { left: 14, right: 14 }
    });

    yPos = doc.lastAutoTable.finalY + 15;

    // Clause-by-Clause Scores
    checkPageBreak(40);
    doc.setFontSize(16);
    doc.setTextColor(20, 184, 166);
    doc.text('Clause Performance', 14, yPos);
    yPos += 10;

    const clauseData = auditClauses.map(clause => {
      const score = calculateClauseScore(clause.id);
      return [
        clause.title,
        `${score.score}%`,
        `${score.answered}/${score.total}`,
        score.yes,
        score.partial,
        score.no
      ];
    });

    autoTable(doc, {
      startY: yPos,
      head: [['Clause', 'Score', 'Progress', 'Yes', 'Partial', 'No']],
      body: clauseData,
      theme: 'grid',
      headStyles: { fillColor: [20, 184, 166] },
      margin: { left: 14, right: 14 },
      columnStyles: {
        0: { cellWidth: 80 },
        1: { cellWidth: 25, halign: 'center' },
        2: { cellWidth: 25, halign: 'center' },
        3: { cellWidth: 15, halign: 'center' },
        4: { cellWidth: 20, halign: 'center' },
        5: { cellWidth: 15, halign: 'center' }
      }
    });

    yPos = doc.lastAutoTable.finalY + 15;

    // Action Plan
    if (actionPlan.length > 0) {
      checkPageBreak(40);
      doc.setFontSize(16);
      doc.setTextColor(20, 184, 166);
      doc.text('My Action Plan', 14, yPos);
      yPos += 10;

      const actionData = actionPlan.map(item => [
        item.completed ? '✓' : '○',
        item.title,
        item.timelineText,
        item.effortHours,
        item.impact.toUpperCase()
      ]);

      autoTable(doc, {
        startY: yPos,
        head: [['Status', 'Action', 'Timeline', 'Effort', 'Impact']],
        body: actionData,
        theme: 'grid',
        headStyles: { fillColor: [20, 184, 166] },
        margin: { left: 14, right: 14 },
        columnStyles: {
          0: { cellWidth: 15, halign: 'center' },
          1: { cellWidth: 85 },
          2: { cellWidth: 30 },
          3: { cellWidth: 25 },
          4: { cellWidth: 25, halign: 'center' }
        }
      });

      yPos = doc.lastAutoTable.finalY + 15;

      // Action Details
      actionPlan.forEach((item, index) => {
        checkPageBreak(30);
        
        doc.setFontSize(12);
        doc.setTextColor(0, 0, 0);
        doc.text(`${index + 1}. ${item.title}`, 14, yPos);
        yPos += 7;

        doc.setFontSize(10);
        doc.setTextColor(100, 100, 100);
        const actionLines = doc.splitTextToSize(item.action, pageWidth - 28);
        doc.text(actionLines, 20, yPos);
        yPos += actionLines.length * 5 + 5;

        if (item.resources && item.resources.length > 0) {
          doc.setTextColor(20, 184, 166);
          doc.text('Resources: ' + item.resources.join(', '), 20, yPos);
          yPos += 7;
        }

        yPos += 3;
      });
    }

    // Footer on last page
    const totalPages = doc.internal.pages.length - 1;
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `Page ${i} of ${totalPages} | ShansoConsult ISO 42001 Assessment | www.shansography.com`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );
    }

    // Save PDF
    doc.save(`ISO42001-ActionPlan-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const exportAssessmentReport = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPos = 20;

    // Helper function
    const checkPageBreak = (requiredSpace) => {
      if (yPos + requiredSpace > pageHeight - 20) {
        doc.addPage();
        yPos = 20;
        return true;
      }
      return false;
    };

    // Add logo
    const logo = new Image();
    logo.src = '/shanso-logo.png';
    doc.addImage(logo, 'PNG', 14, 8, 40, 15); // x, y, width, height

    // Header
    doc.setFillColor(20, 184, 166);
    doc.rect(0, 0, pageWidth, 35, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(22);
    doc.text('ISO 42001 Assessment Report', pageWidth / 2, 15, { align: 'center' });
    doc.setFontSize(12);
    doc.text('AI Management System Compliance Audit', pageWidth / 2, 25, { align: 'center' });
    
    yPos = 45;

    // Organization Info Section
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text(`Assessment Date: ${new Date().toLocaleDateString()}`, 14, yPos);
    yPos += 6;
    doc.text(`Generated by: ShansoConsult ISO 42001 Tools`, 14, yPos);
    yPos += 15;

    // Executive Summary
    doc.setFontSize(16);
    doc.setTextColor(20, 184, 166);
    doc.text('Executive Summary', 14, yPos);
    yPos += 10;

    const overallScore = calculateAuditScore();
    const stats = getOverallStats();
    
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    
    // Score box
    doc.setFillColor(240, 240, 240);
    doc.rect(14, yPos, pageWidth - 28, 25, 'F');
    doc.setFontSize(14);
    doc.setTextColor(20, 184, 166);
    doc.text(`Overall Compliance Score: ${overallScore}%`, pageWidth / 2, yPos + 10, { align: 'center' });
    doc.setFontSize(12);
    doc.text(`Status: ${getScoreLabel(overallScore)}`, pageWidth / 2, yPos + 18, { align: 'center' });
    yPos += 35;

    // Key Metrics
    autoTable(doc, {
      startY: yPos,
      head: [['Metric', 'Count', 'Percentage']],
      body: [
        ['Total Questions', stats.total, '100%'],
        ['Answered', stats.answered, `${Math.round((stats.answered/stats.total)*100)}%`],
        ['Fully Compliant (Yes)', stats.yes, `${Math.round((stats.yes/stats.total)*100)}%`],
        ['Partially Compliant', stats.partial, `${Math.round((stats.partial/stats.total)*100)}%`],
        ['Non-Compliant (No)', stats.no, `${Math.round((stats.no/stats.total)*100)}%`],
        ['Unanswered', stats.unanswered, `${Math.round((stats.unanswered/stats.total)*100)}%`]
      ],
      theme: 'striped',
      headStyles: { fillColor: [20, 184, 166] },
      margin: { left: 14, right: 14 }
    });

    yPos = doc.lastAutoTable.finalY + 15;
    checkPageBreak(40);

    // Clause Performance
    doc.setFontSize(16);
    doc.setTextColor(20, 184, 166);
    doc.text('Detailed Clause Analysis', 14, yPos);
    yPos += 10;

    auditClauses.forEach((clause, index) => {
      checkPageBreak(60);
      
      const score = calculateClauseScore(clause.id);
      
      doc.setFontSize(13);
      doc.setTextColor(0, 0, 0);
      doc.text(clause.title, 14, yPos);
      yPos += 8;

      // Score bar visualization
      const barWidth = 100;
      const scoreWidth = (score.score / 100) * barWidth;
      
      doc.setFillColor(220, 220, 220);
      doc.rect(14, yPos, barWidth, 6, 'F');
      
      const barColor = score.score >= 75 ? [34, 197, 94] : score.score >= 50 ? [234, 179, 8] : [239, 68, 68];
      doc.setFillColor(barColor[0], barColor[1], barColor[2]);
      doc.rect(14, yPos, scoreWidth, 6, 'F');
      
      doc.setFontSize(10);
      doc.text(`${score.score}%`, 120, yPos + 4);
      yPos += 10;

      autoTable(doc, {
        startY: yPos,
        head: [['Metric', 'Value']],
        body: [
          ['Progress', `${score.answered}/${score.total} questions answered`],
          ['Fully Compliant', `${score.yes} (${Math.round((score.yes/score.total)*100)}%)`],
          ['Partially Compliant', `${score.partial} (${Math.round((score.partial/score.total)*100)}%)`],
          ['Non-Compliant', `${score.no} (${Math.round((score.no/score.total)*100)}%)`]
        ],
        theme: 'plain',
        margin: { left: 20, right: 14 },
        styles: { fontSize: 9 }
      });

      yPos = doc.lastAutoTable.finalY + 10;
    });

    // Recommendations
    checkPageBreak(40);
    doc.setFontSize(16);
    doc.setTextColor(20, 184, 166);
    doc.text('Recommendations', 14, yPos);
    yPos += 10;

    const recommendations = getRecommendations();
    if (recommendations.length > 0) {
      recommendations.forEach((rec, index) => {
        checkPageBreak(20);
        
        doc.setFontSize(11);
        doc.setTextColor(0, 0, 0);
        
        const icon = rec.type === 'critical' ? '⚠️' : rec.type === 'warning' ? '⚡' : rec.type === 'success' ? '✓' : 'ℹ️';
        doc.text(`${icon} ${rec.title}`, 14, yPos);
        yPos += 7;
        
        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        const messageLines = doc.splitTextToSize(rec.message, pageWidth - 28);
        doc.text(messageLines, 20, yPos);
        yPos += messageLines.length * 5 + 8;
      });
    }

    // Footer
    const totalPages = doc.internal.pages.length - 1;
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150, 150, 150);
      doc.text(
        `Page ${i} of ${totalPages} | ShansoConsult | ISO 42001 Assessment | Confidential`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );
    }

    doc.save(`ISO42001-Assessment-Report-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const getRiskColor = (severity, likelihood) => {
    if ((severity === 'critical' && likelihood !== 'low') || (severity === 'high' && likelihood === 'high')) {
      return 'bg-red-500/20 border-red-500/50 text-red-400';
    }
    if (severity === 'high' || (severity === 'medium' && likelihood === 'high')) {
      return 'bg-orange-500/20 border-orange-500/50 text-orange-400';
    }
    return 'bg-yellow-500/20 border-yellow-500/50 text-yellow-400';
  };

  const getPhaseStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'in-progress':
        return <ArrowRight className="w-5 h-5 text-cyan-400 animate-pulse" />;
      default:
        return <div className="w-5 h-5 rounded-full border-2 border-gray-600" />;
    }
  };

  // NEW: Generate AI Recommendations
  const generateAIRecommendations = async () => {
    setIsGeneratingAI(true);
    setAiError(null);
    
    try {
      // Check if assessment has any responses
      const totalResponses = Object.keys(auditResponses).length;
      if (totalResponses === 0) {
        setAiError('Please complete the assessment first before generating recommendations.');
        setIsGeneratingAI(false);
        return;
      }
      
      // Debug: Log all responses to see what we have
      console.log('All audit responses:', auditResponses);
      console.log('Sample responses:', Object.entries(auditResponses).slice(0, 5));
      
      // Identify gaps (Partial or No answers) - case insensitive check
      const gaps = [];
      auditClauses.forEach((clause, clauseIndex) => {
        clause.questions.forEach((q, questionIndex) => {
          // FIX: Use the same key format as when responses are saved
          const responseKey = `${clause.id}-${questionIndex}`;
          const response = auditResponses[responseKey];
          
          // Debug: Log each check
          if (response) {
            const lowerResponse = response.toLowerCase();
            if (lowerResponse === 'partial' || lowerResponse === 'no') {
              console.log(`Found gap: ${q.q.substring(0, 50)}... = ${response}`);
              gaps.push({
                clause: clause.title,
                question: q.q,
                status: response,
                answer: q.a
              });
            }
          }
        });
      });
      
      console.log(`Found ${gaps.length} gaps out of ${totalResponses} responses`);
      console.log('Response types:', {
        yes: Object.values(auditResponses).filter(r => r && r.toLowerCase() === 'yes').length,
        no: Object.values(auditResponses).filter(r => r && r.toLowerCase() === 'no').length,
        partial: Object.values(auditResponses).filter(r => r && r.toLowerCase() === 'partial').length
      });
      
      if (gaps.length === 0) {
        setAiError(`You've answered ${totalResponses} questions, but all responses are "Yes". To get AI recommendations, answer some questions with "Partial" or "No" to identify improvement areas.`);
        setIsGeneratingAI(false);
        return;
      }
      
      // Call backend API
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/ai-recommendations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          audit_responses: auditResponses,
          industry: userIndustry,
          company_size: companySize,
          current_score: calculateAuditScore(),
          gaps: gaps
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.detail || errorData.message || response.statusText;
        
        // Check for budget-related errors
        if (errorMessage.includes('Budget') || errorMessage.includes('budget') || errorMessage.includes('exceeded')) {
          throw new Error('Universal Key budget exceeded. Please add balance in Profile → Universal Key → Add Balance, then try again.');
        }
        
        throw new Error(`Failed to generate recommendations: ${errorMessage}`);
      }
      
      const data = await response.json();
      setAiRecommendations(data);
      
      // Save to localStorage
      localStorage.setItem('iso42001_ai_recommendations', JSON.stringify(data));
      
    } catch (error) {
      console.error('Error generating AI recommendations:', error);
      setAiError(error.message || 'Failed to generate recommendations. Please try again.');
    } finally {
      setIsGeneratingAI(false);
    }
  };

  // Load saved AI recommendations
  useEffect(() => {
    const saved = localStorage.getItem('iso42001_ai_recommendations');
    if (saved) {
      try {
        setAiRecommendations(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to parse saved AI recommendations');
      }
    }
  }, []);

  return (
    <div className="iso42001-tools-page">
      {/* Theme Toggle Button */}
      <button
        onClick={toggleTheme}
        className="theme-toggle"
        aria-label="Toggle theme"
      >
        {isDarkMode ? <Sun className="w-6 h-6 text-white" /> : <Moon className="w-6 h-6 text-white" />}
      </button>

      {/* Email Capture Modal */}
      <EmailCaptureModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        onSave={(email) => {
          setUserEmail(email);
          setShowEmailModal(false);
        }}
      />

      {/* Achievement Notification */}
      {newAchievement && (
        <AchievementNotification
          achievement={newAchievement}
          onClose={() => setNewAchievement(null)}
        />
      )}

      {/* Hero Section - Enhanced */}
      <section className="hero-section relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-blue-900/20 to-gray-900"></div>
        
        {/* Animated background elements - Enhanced */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl animate-pulse-slow animate-float"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-purple-500/5 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-3 mb-6 animate-fade-in-up">
            <Shield className="w-12 h-12 text-cyan-400 animate-bounce-gentle" />
            <span className="text-sm font-semibold text-cyan-400 bg-cyan-400/10 px-4 py-2 rounded-full border border-cyan-400/30">
              ISO/IEC 42001:2023 Certified Implementation
            </span>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold mb-6 animate-fade-in-up stagger-1">
            <span className="text-white">Operationalize AI with</span>
            <br />
            <span className="hero-gradient-text">
              ISO 42001. Master ISO 42001.
            </span>
          </h1>
          
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto animate-fade-in-up stagger-2">
            Expert consultation and audit readiness for organizations building the future. 
            From ISO 42001 compliance to cutting-edge AI governance frameworks.
          </p>

          {/* NEW: Progress Badge */}
          {Object.keys(auditResponses).length > 0 && (
            <div className="mb-6 animate-fade-in-up stagger-3">
              <div className="inline-block bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 rounded-full px-6 py-3">
                <div className="flex items-center gap-3">
                  <Award className="w-5 h-5 text-yellow-400" />
                  <span className="text-white font-semibold">
                    {calculateTotalPoints(userAchievements)} Points Earned
                  </span>
                  <span className="text-gray-400">•</span>
                  <span className="text-cyan-400 font-semibold">
                    {calculateProgress(auditResponses, auditClauses.reduce((sum, c) => sum + c.questions.length, 0))}% Complete
                  </span>
                </div>
              </div>
            </div>
          )}
          
          <div className="flex gap-4 justify-center flex-wrap animate-fade-in-up stagger-4">
            <Button 
              onClick={() => {
                setActiveSection('audit');
                // Scroll to the navigation tabs section
                setTimeout(() => {
                  const tabsSection = document.querySelector('.sticky.top-0');
                  if (tabsSection) {
                    tabsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }, 100);
              }}
              className="px-8 py-6 text-lg bg-cyan-500 hover:bg-cyan-600 transform hover:scale-105 transition-all"
            >
              Launch Your Readiness Assessment
            </Button>
            <Button 
              onClick={() => window.location.href = '/pricing'}
              className="px-8 py-6 text-lg bg-transparent border-2 border-cyan-500 hover:bg-cyan-500/10"
            >
              View Roadmaps
            </Button>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <div className="sticky top-0 z-40 bg-gray-900/95 backdrop-blur-md border-b border-gray-800">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex gap-2 overflow-x-auto">
            <button
              onClick={() => setActiveSection('audit')}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all whitespace-nowrap ${
                activeSection === 'audit'
                  ? 'bg-cyan-500 text-white'
                  : 'bg-gray-800/50 text-gray-400 hover:bg-gray-800'
              }`}
            >
              <FileText className="w-5 h-5" />
              Deep Dive Audit
            </button>
            <button
              onClick={() => setActiveSection('ai-recommendations')}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all whitespace-nowrap relative ${
                activeSection === 'ai-recommendations'
                  ? 'bg-cyan-500 text-white'
                  : 'bg-gray-800/50 text-gray-400 hover:bg-gray-800'
              }`}
            >
              <Star className="w-5 h-5" />
              AI Recommendations
              <span className="absolute -top-1 -right-1 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                AI
              </span>
            </button>
            <button
              onClick={() => setActiveSection('heatmap')}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all whitespace-nowrap ${
                activeSection === 'heatmap'
                  ? 'bg-cyan-500 text-white'
                  : 'bg-gray-800/50 text-gray-400 hover:bg-gray-800'
              }`}
            >
              <BarChart3 className="w-5 h-5" />
              Risk Heatmap
            </button>
            <button
              onClick={() => setActiveSection('circuit')}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all whitespace-nowrap ${
                activeSection === 'circuit'
                  ? 'bg-cyan-500 text-white'
                  : 'bg-gray-800/50 text-gray-400 hover:bg-gray-800'
              }`}
            >
              <Shield className="w-5 h-5" />
              Implementation Circuit
            </button>
            <button
              onClick={() => setActiveSection('quickwins')}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all whitespace-nowrap relative ${
                activeSection === 'quickwins'
                  ? 'bg-cyan-500 text-white'
                  : 'bg-gray-800/50 text-gray-400 hover:bg-gray-800'
              }`}
            >
              <Zap className="w-5 h-5" />
              Quick Wins
              {getQuickWinsCount() > 0 && (
                <span className="absolute -top-2 -right-2 bg-yellow-500 text-black text-xs font-bold rounded-full w-6 h-6 flex items-center justify-center">
                  {getQuickWinsCount()}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveSection('benchmark')}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all whitespace-nowrap ${
                activeSection === 'benchmark'
                  ? 'bg-cyan-500 text-white'
                  : 'bg-gray-800/50 text-gray-400 hover:bg-gray-800'
              }`}
            >
              <BarChart2 className="w-5 h-5" />
              Benchmark
            </button>
            <button
              onClick={() => setActiveSection('learn')}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all whitespace-nowrap ${
                activeSection === 'learn'
                  ? 'bg-cyan-500 text-white'
                  : 'bg-gray-800/50 text-gray-400 hover:bg-gray-800'
              }`}
            >
              <BookOpen className="w-5 h-5" />
              Learn
            </button>
          </div>
        </div>
      </div>

      {/* ISO 42001 Deep Dive Audit */}
      {activeSection === 'audit' && (
        <section className="py-20 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-white mb-4">ISO 42001 Deep Dive Audit</h2>
              <p className="text-xl text-gray-400">
                Comprehensive assessment of your AI Management System readiness
              </p>
            </div>

            {/* Interactive Score Summary Panel */}
            <div className="mb-12">
              <button
                onClick={() => setShowScoreSummary(!showScoreSummary)}
                className="w-full bg-gray-800/50 backdrop-blur-md border border-cyan-500/30 rounded-2xl p-6 hover:bg-gray-800/70 transition-all mb-4"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4 flex-1">
                    <BarChart3 className="w-8 h-8 text-cyan-400" />
                    <div className="text-left flex-1">
                      <h3 className="text-2xl font-bold text-white">Assessment Summary</h3>
                      <div className="flex items-center gap-4 flex-wrap">
                        <p className="text-gray-400">Overall Compliance: {calculateAuditScore()}% - {getScoreLabel(calculateAuditScore())}</p>
                        {getQuickWinsCount() > 0 && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveSection('quickwins');
                            }}
                            className="flex items-center gap-2 px-3 py-1 bg-yellow-500/20 border border-yellow-500/50 rounded-full text-yellow-400 text-sm font-semibold hover:bg-yellow-500/30 transition-all"
                          >
                            <Zap className="w-4 h-4" />
                            {getQuickWinsCount()} Quick Wins Available
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  {showScoreSummary ? <ChevronUp className="w-6 h-6 text-cyan-400" /> : <ChevronDown className="w-6 h-6 text-cyan-400" />}
                </div>
              </button>

              {showScoreSummary && (
                <div className="bg-gray-800/50 backdrop-blur-md border border-gray-700/50 rounded-2xl p-8">
                  <div className="grid lg:grid-cols-2 gap-8 mb-8">
                    {/* Left: Overall Score & Stats */}
                    <div>
                      <div className="text-center mb-8">
                        <div className={`inline-block px-8 py-12 rounded-2xl border-2 ${getScoreColor(calculateAuditScore())}`}>
                          <div className="text-7xl font-bold mb-2">{calculateAuditScore()}%</div>
                          <div className="text-xl font-semibold">{getScoreLabel(calculateAuditScore())}</div>
                        </div>
                      </div>

                      {/* Progress Bar */}
                      <div className="mb-8">
                        <div className="flex justify-between text-sm text-gray-400 mb-2">
                          <span>Progress</span>
                          <span>{getOverallStats().answered}/{getOverallStats().total} answered</span>
                        </div>
                        <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-cyan-500 to-blue-500 transition-all duration-500"
                            style={{ width: `${(getOverallStats().answered / getOverallStats().total) * 100}%` }}
                          ></div>
                        </div>
                      </div>

                      {/* Breakdown Stats */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <CheckCircle className="w-5 h-5 text-green-400" />
                            <span className="text-sm text-gray-400">Fully Compliant</span>
                          </div>
                          <div className="text-3xl font-bold text-green-400">{getOverallStats().yes}</div>
                          <div className="text-xs text-gray-500">{Math.round((getOverallStats().yes / getOverallStats().total) * 100)}%</div>
                        </div>

                        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <AlertTriangle className="w-5 h-5 text-yellow-400" />
                            <span className="text-sm text-gray-400">Partial</span>
                          </div>
                          <div className="text-3xl font-bold text-yellow-400">{getOverallStats().partial}</div>
                          <div className="text-xs text-gray-500">{Math.round((getOverallStats().partial / getOverallStats().total) * 100)}%</div>
                        </div>

                        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <XCircle className="w-5 h-5 text-red-400" />
                            <span className="text-sm text-gray-400">Non-Compliant</span>
                          </div>
                          <div className="text-3xl font-bold text-red-400">{getOverallStats().no}</div>
                          <div className="text-xs text-gray-500">{Math.round((getOverallStats().no / getOverallStats().total) * 100)}%</div>
                        </div>

                        <div className="bg-gray-500/10 border border-gray-500/30 rounded-xl p-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Target className="w-5 h-5 text-gray-400" />
                            <span className="text-sm text-gray-400">Unanswered</span>
                          </div>
                          <div className="text-3xl font-bold text-gray-400">{getOverallStats().unanswered}</div>
                          <div className="text-xs text-gray-500">{Math.round((getOverallStats().unanswered / getOverallStats().total) * 100)}%</div>
                        </div>
                      </div>
                    </div>

                    {/* Right: Radar Chart */}
                    <div>
                      <h4 className="text-xl font-bold text-white mb-4 text-center">Compliance by Clause</h4>
                      <ResponsiveContainer width="100%" height={300}>
                        <RadarChart data={getRadarChartData()}>
                          <PolarGrid stroke="#374151" />
                          <PolarAngleAxis dataKey="clause" tick={{ fill: '#9CA3AF', fontSize: 12 }} />
                          <PolarRadiusAxis angle={90} domain={[0, 100]} tick={{ fill: '#9CA3AF' }} />
                          <Radar name="Score" dataKey="score" stroke="#14b8a6" fill="#14b8a6" fillOpacity={0.3} />
                          <Legend wrapperStyle={{ paddingTop: '20px' }} />
                        </RadarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Clause-by-Clause Scores */}
                  <div className="mb-8">
                    <h4 className="text-xl font-bold text-white mb-4">📊 Clause Performance</h4>
                    <div className="space-y-3">
                      {auditClauses.map(clause => {
                        const clauseScore = calculateClauseScore(clause.id);
                        return (
                          <div key={clause.id} className="bg-gray-900/50 rounded-xl p-4">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-white font-semibold">{clause.title}</span>
                              <span className={`px-3 py-1 rounded-full text-sm font-bold ${getScoreColor(clauseScore.score)}`}>
                                {clauseScore.score}%
                              </span>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-gray-400 mb-2">
                              <span>{clauseScore.answered}/{clauseScore.total} answered</span>
                              <span className="text-green-400">✓ {clauseScore.yes}</span>
                              <span className="text-yellow-400">⚠ {clauseScore.partial}</span>
                              <span className="text-red-400">✗ {clauseScore.no}</span>
                            </div>
                            <div className="w-full bg-gray-700 rounded-full h-2 overflow-hidden">
                              <div 
                                className={`h-full transition-all duration-500 ${
                                  clauseScore.score >= 75 ? 'bg-green-500' :
                                  clauseScore.score >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                                }`}
                                style={{ width: `${clauseScore.score}%` }}
                              ></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Recommendations */}
                  {getRecommendations().length > 0 && (
                    <div className="mb-8">
                      <h4 className="text-xl font-bold text-white mb-4">💡 Recommendations</h4>
                      <div className="space-y-3">
                        {getRecommendations().map((rec, index) => (
                          <div 
                            key={index}
                            className={`rounded-xl p-4 border ${
                              rec.type === 'critical' ? 'bg-red-500/10 border-red-500/30' :
                              rec.type === 'warning' ? 'bg-yellow-500/10 border-yellow-500/30' :
                              rec.type === 'success' ? 'bg-green-500/10 border-green-500/30' :
                              'bg-blue-500/10 border-blue-500/30'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              {rec.type === 'critical' && <XCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-1" />}
                              {rec.type === 'warning' && <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-1" />}
                              {rec.type === 'success' && <Award className="w-5 h-5 text-green-400 flex-shrink-0 mt-1" />}
                              {rec.type === 'info' && <TrendingUp className="w-5 h-5 text-blue-400 flex-shrink-0 mt-1" />}
                              <div>
                                <h5 className="font-semibold text-white mb-1">{rec.title}</h5>
                                <p className="text-sm text-gray-400">{rec.message}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-4 justify-center flex-wrap">
                    <Button 
                      onClick={exportAssessmentReport}
                      className="px-6 py-3 bg-cyan-500 hover:bg-cyan-600"
                    >
                      <Download className="w-5 h-5 mr-2" />
                      Export Report (PDF)
                    </Button>
                    <Button 
                      onClick={() => {
                        if (window.confirm('Are you sure you want to reset all responses?')) {
                          setAuditResponses({});
                        }
                      }}
                      className="px-6 py-3 bg-gray-700 hover:bg-gray-600"
                    >
                      Reset Assessment
                    </Button>
                    <Button 
                      onClick={() => window.location.href = '/book-consultation'}
                      className="px-6 py-3 bg-transparent border-2 border-cyan-500 hover:bg-cyan-500/10"
                    >
                      Book Strategy Call
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Audit Clauses */}
            <div className="space-y-6">
              {auditClauses.map((clause) => (
                <div
                  key={clause.id}
                  className="bg-gray-800/50 backdrop-blur-md rounded-2xl border border-gray-700/50 overflow-hidden"
                >
                  <button
                    onClick={() => setSelectedClause(selectedClause === clause.id ? null : clause.id)}
                    className="w-full px-8 py-6 flex items-center justify-between hover:bg-gray-700/30 transition-all"
                  >
                    <div className="flex items-center gap-4 flex-1">
                      <div className="w-12 h-12 rounded-lg bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center flex-shrink-0">
                        <FileText className="w-6 h-6 text-cyan-400" />
                      </div>
                      <h3 className="text-xl font-semibold text-white text-left flex-1">{clause.title}</h3>
                      <div className="flex items-center gap-3">
                        <span className={`px-4 py-2 rounded-lg text-sm font-bold border ${getScoreColor(calculateClauseScore(clause.id).score)}`}>
                          {calculateClauseScore(clause.id).score}%
                        </span>
                        <span className="text-sm text-gray-400">
                          {calculateClauseScore(clause.id).answered}/{calculateClauseScore(clause.id).total}
                        </span>
                      </div>
                    </div>
                    <ArrowRight className={`w-6 h-6 text-cyan-400 transition-transform ml-4 ${selectedClause === clause.id ? 'rotate-90' : ''}`} />
                  </button>

                  {selectedClause === clause.id && (
                    <div className="px-8 pb-8 space-y-4">
                      {clause.questions.map((question, index) => (
                        <div key={index} className="bg-gray-900/50 rounded-xl p-6 border border-gray-700/50">
                          <div className="mb-4">
                            <p className="text-white font-semibold mb-3 text-lg">{index + 1}. {question.q}</p>
                            <div className="bg-cyan-500/5 border border-cyan-500/20 rounded-lg p-4 mb-4">
                              <p className="text-sm text-cyan-400 font-semibold mb-2">💡 Guidance:</p>
                              <p className="text-gray-300 text-sm leading-relaxed">{question.a}</p>
                            </div>
                          </div>
                          <div className="flex gap-3 flex-wrap">
                            <button
                              onClick={() => handleAuditResponse(clause.id, index, 'yes')}
                              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${
                                auditResponses[`${clause.id}-${index}`] === 'yes'
                                  ? 'bg-green-500 text-white'
                                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                              }`}
                            >
                              <CheckCircle className="w-5 h-5" />
                              Yes
                            </button>
                            <button
                              onClick={() => handleAuditResponse(clause.id, index, 'partial')}
                              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${
                                auditResponses[`${clause.id}-${index}`] === 'partial'
                                  ? 'bg-yellow-500 text-white'
                                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                              }`}
                            >
                              <AlertTriangle className="w-5 h-5" />
                              Partial
                            </button>
                            <button
                              onClick={() => handleAuditResponse(clause.id, index, 'no')}
                              className={`flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all ${
                                auditResponses[`${clause.id}-${index}`] === 'no'
                                  ? 'bg-red-500 text-white'
                                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                              }`}
                            >
                              <XCircle className="w-5 h-5" />
                              No
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>

          </div>
        </section>
      )}

      {/* Interactive Risk Heatmap */}
      {activeSection === 'heatmap' && (
        <section className="py-20 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-white mb-4">Interactive Risk Heatmap</h2>
              <p className="text-xl text-gray-400">
                Visualize and prioritize AI-related risks across your organization
              </p>
            </div>

            {/* Risk Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {riskCategories.map((risk) => (
                <div
                  key={risk.id}
                  className={`rounded-2xl border-2 p-6 transition-all hover:scale-105 cursor-pointer ${getRiskColor(risk.severity, risk.likelihood)}`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <AlertTriangle className="w-8 h-8" />
                    <div className="flex gap-2">
                      <span className="text-xs px-2 py-1 rounded-full bg-black/30 capitalize">
                        {risk.severity}
                      </span>
                      <span className="text-xs px-2 py-1 rounded-full bg-black/30 capitalize">
                        {risk.likelihood}
                      </span>
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{risk.name}</h3>
                  <p className="text-sm opacity-80">
                    Click to view mitigation strategies and controls
                  </p>
                </div>
              ))}
            </div>

            {/* Risk Matrix Legend */}
            <div className="bg-gray-800/50 backdrop-blur-md rounded-2xl border border-gray-700/50 p-8">
              <h3 className="text-2xl font-bold text-white mb-6">Risk Assessment Criteria</h3>
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h4 className="text-lg font-semibold text-cyan-400 mb-4">Severity Levels</h4>
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <div className="w-4 h-4 rounded bg-red-500"></div>
                      <span className="text-gray-300">Critical - System-wide impact</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-4 h-4 rounded bg-orange-500"></div>
                      <span className="text-gray-300">High - Significant impact</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-4 h-4 rounded bg-yellow-500"></div>
                      <span className="text-gray-300">Medium - Moderate impact</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-cyan-400 mb-4">Likelihood Levels</h4>
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <div className="w-4 h-4 rounded bg-red-500"></div>
                      <span className="text-gray-300">High - Very likely to occur</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-4 h-4 rounded bg-orange-500"></div>
                      <span className="text-gray-300">Medium - May occur</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-4 h-4 rounded bg-yellow-500"></div>
                      <span className="text-gray-300">Low - Unlikely to occur</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Implementation Circuit */}
      {activeSection === 'circuit' && (
        <section className="py-20 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-white mb-4">Implementation Circuit</h2>
              <p className="text-xl text-gray-400">
                Your roadmap to ISO 42001 certification success
              </p>
            </div>

            {/* Implementation Phases */}
            <div className="relative">
              {/* Connecting Line */}
              <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-cyan-500 via-blue-500 to-cyan-500"></div>

              <div className="space-y-8">
                {implementationPhases.map((phase, index) => (
                  <div key={index} className="relative pl-20">
                    {/* Phase Icon */}
                    <div className="absolute left-0 top-0 w-16 h-16 rounded-full bg-gray-800 border-4 border-gray-900 flex items-center justify-center">
                      {getPhaseStatusIcon(phase.status)}
                    </div>

                    {/* Phase Card */}
                    <div className="bg-gray-800/50 backdrop-blur-md rounded-2xl border border-gray-700/50 p-8 hover:border-cyan-500/50 transition-all">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-2xl font-bold text-white mb-2">{phase.phase}</h3>
                          <p className="text-gray-400 text-lg">{phase.description}</p>
                        </div>
                        <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
                          phase.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                          phase.status === 'in-progress' ? 'bg-cyan-500/20 text-cyan-400' :
                          'bg-gray-700/50 text-gray-400'
                        }`}>
                          {phase.status === 'completed' ? 'Completed' :
                           phase.status === 'in-progress' ? 'In Progress' :
                           'Pending'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        <span>⏱️ Duration:</span>
                        <span className="font-semibold">{phase.duration}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA Section */}
            <div className="mt-16 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 rounded-2xl p-12 text-center">
              <h3 className="text-3xl font-bold text-white mb-4">
                Ready to Start Your ISO 42001 Journey?
              </h3>
              <p className="text-xl text-gray-400 mb-8">
                Schedule a free consultation to discuss your AI governance needs
              </p>
              <div className="flex gap-4 justify-center flex-wrap">
                <Button 
                  onClick={() => window.location.href = '/book-consultation'}
                  className="px-8 py-6 text-lg bg-cyan-500 hover:bg-cyan-600"
                >
                  Book Strategy Call
                </Button>
                <Button 
                  onClick={() => window.location.href = '/pricing'}
                  className="px-8 py-6 text-lg bg-transparent border-2 border-cyan-500 hover:bg-cyan-500/10"
                >
                  View Pricing
                </Button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Quick Wins Section */}
      {activeSection === 'quickwins' && (
        <section className="py-20 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-white mb-4">⚡ Quick Wins Dashboard</h2>
              <p className="text-xl text-gray-400">
                Actionable improvements based on your assessment
              </p>
              <div className="mt-4 inline-block bg-yellow-500/10 border border-yellow-500/30 px-6 py-3 rounded-full">
                <span className="text-yellow-400 font-semibold">{getQuickWinsCount()} Quick Wins Available</span>
              </div>
            </div>

            {getQuickWinsCount() === 0 ? (
              <div className="text-center py-20">
                <Award className="w-16 h-16 text-green-400 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-white mb-2">Excellent Work!</h3>
                <p className="text-gray-400">
                  You haven't identified any gaps yet. Answer questions with "Partial" or "No" to see improvement suggestions.
                </p>
              </div>
            ) : (
              <>
                {/* Filter Options */}
                <div className="mb-8 flex gap-3 justify-center flex-wrap">
                  <button
                    onClick={() => setFilterBy('priority')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all ${
                      filterBy === 'priority' ? 'bg-cyan-500 text-white' : 'bg-gray-800 text-gray-400'
                    }`}
                  >
                    <AlertTriangle className="w-4 h-4" />
                    By Priority
                  </button>
                  <button
                    onClick={() => setFilterBy('timeline')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all ${
                      filterBy === 'timeline' ? 'bg-cyan-500 text-white' : 'bg-gray-800 text-gray-400'
                    }`}
                  >
                    <Clock className="w-4 h-4" />
                    By Timeline
                  </button>
                  <button
                    onClick={() => setFilterBy('effort')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all ${
                      filterBy === 'effort' ? 'bg-cyan-500 text-white' : 'bg-gray-800 text-gray-400'
                    }`}
                  >
                    <Briefcase className="w-4 h-4" />
                    By Effort
                  </button>
                  <button
                    onClick={() => setFilterBy('clause')}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all ${
                      filterBy === 'clause' ? 'bg-cyan-500 text-white' : 'bg-gray-800 text-gray-400'
                    }`}
                  >
                    <Filter className="w-4 h-4" />
                    By Clause
                  </button>
                </div>

                {/* Quick Wins List */}
                <div className="space-y-8">
                  {(() => {
                    const filteredWins = getFilteredQuickWins();
                    const groups = {};
                    filteredWins.forEach(qw => {
                      if (!groups[qw.group]) groups[qw.group] = [];
                      groups[qw.group].push(qw);
                    });

                    return Object.entries(groups).map(([groupName, wins]) => (
                      <div key={groupName}>
                        <h3 className="text-2xl font-bold text-cyan-400 mb-4 flex items-center gap-2">
                          {groupName.includes('Critical') && <AlertTriangle className="w-6 h-6 text-red-400" />}
                          {groupName.includes('Immediate') && <Zap className="w-6 h-6 text-yellow-400" />}
                          {groupName}
                          <span className="text-sm text-gray-400">({wins.length})</span>
                        </h3>
                        
                        <div className="space-y-4">
                          {wins.map((quickWin) => (
                            <div
                              key={quickWin.id}
                              className="bg-gray-800/50 backdrop-blur-md rounded-xl border border-gray-700/50 p-6 hover:border-cyan-500/50 transition-all"
                            >
                              <div className="flex items-start justify-between gap-4 mb-4">
                                <div className="flex-1">
                                  <h4 className="text-xl font-bold text-white mb-2">{quickWin.title}</h4>
                                  <p className="text-gray-300 mb-4">{quickWin.action}</p>
                                  
                                  {/* Metadata */}
                                  <div className="flex gap-4 flex-wrap text-sm">
                                    <div className="flex items-center gap-2 bg-cyan-500/10 border border-cyan-500/30 px-3 py-1 rounded-lg">
                                      <Clock className="w-4 h-4 text-cyan-400" />
                                      <span className="text-cyan-400">{quickWin.timelineText}</span>
                                    </div>
                                    <div className={`flex items-center gap-2 px-3 py-1 rounded-lg border ${
                                      quickWin.effort === 'low' ? 'bg-green-500/10 border-green-500/30' :
                                      quickWin.effort === 'medium' ? 'bg-yellow-500/10 border-yellow-500/30' :
                                      'bg-red-500/10 border-red-500/30'
                                    }`}>
                                      <Briefcase className="w-4 h-4" />
                                      <span>{quickWin.effortHours}</span>
                                    </div>
                                    <div className={`flex items-center gap-2 px-3 py-1 rounded-lg border ${
                                      quickWin.impact === 'critical' ? 'bg-red-500/10 border-red-500/30 text-red-400' :
                                      quickWin.impact === 'high' ? 'bg-orange-500/10 border-orange-500/30 text-orange-400' :
                                      'bg-yellow-500/10 border-yellow-500/30 text-yellow-400'
                                    }`}>
                                      <TrendingUp className="w-4 h-4" />
                                      <span className="capitalize">{quickWin.impact} Impact</span>
                                    </div>
                                  </div>
                                  
                                  {/* Resources */}
                                  {quickWin.resources && quickWin.resources.length > 0 && (
                                    <div className="mt-4">
                                      <p className="text-sm text-gray-400 mb-2">📎 Resources:</p>
                                      <div className="flex gap-2 flex-wrap">
                                        {quickWin.resources.map((resource, idx) => (
                                          <button
                                            key={idx}
                                            onClick={() => downloadTemplate(resource)}
                                            className="text-xs px-3 py-1 bg-gray-700 hover:bg-gray-600 text-cyan-400 rounded-lg transition-all flex items-center gap-1"
                                          >
                                            <Download className="w-3 h-3" />
                                            {resource}
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                                
                                {/* Action Button */}
                                <Button
                                  onClick={() => isInActionPlan(quickWin.id) ? removeFromActionPlan(quickWin.id) : addToActionPlan(quickWin)}
                                  className={`flex items-center gap-2 whitespace-nowrap ${
                                    isInActionPlan(quickWin.id)
                                      ? 'bg-green-500 hover:bg-green-600'
                                      : 'bg-cyan-500 hover:bg-cyan-600'
                                  }`}
                                >
                                  {isInActionPlan(quickWin.id) ? (
                                    <>
                                      <Check className="w-4 h-4" />
                                      In Plan
                                    </>
                                  ) : (
                                    <>
                                      <Plus className="w-4 h-4" />
                                      Add to Plan
                                    </>
                                  )}
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ));
                  })()}
                </div>

                {/* My Action Plan Section */}
                {actionPlan.length > 0 && (
                  <div className="mt-16 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 rounded-2xl p-8">
                    <div className="flex items-center justify-between mb-6">
                      <h3 className="text-3xl font-bold text-white flex items-center gap-3">
                        📋 My Action Plan
                        <span className="text-sm text-cyan-400">({actionPlan.length} items)</span>
                      </h3>
                      <Button
                        onClick={exportActionPlan}
                        className="px-6 py-3 bg-cyan-500 hover:bg-cyan-600"
                      >
                        <Download className="w-5 h-5 mr-2" />
                        Export Plan
                      </Button>
                    </div>
                    
                    <div className="space-y-3">
                      {actionPlan.map((item) => (
                        <div
                          key={item.id}
                          className={`flex items-center gap-4 p-4 rounded-xl transition-all ${
                            item.completed
                              ? 'bg-green-500/10 border border-green-500/30'
                              : 'bg-gray-900/50 border border-gray-700/50'
                          }`}
                        >
                          <button
                            onClick={() => toggleQuickWinComplete(item.id)}
                            className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${
                              item.completed
                                ? 'bg-green-500 border-green-500'
                                : 'border-gray-600 hover:border-cyan-500'
                            }`}
                          >
                            {item.completed && <Check className="w-4 h-4 text-white" />}
                          </button>
                          <div className="flex-1">
                            <p className={`font-semibold ${item.completed ? 'line-through text-gray-500' : 'text-white'}`}>
                              {item.title}
                            </p>
                            <p className="text-sm text-gray-400">{item.timelineText} • {item.effortHours}</p>
                          </div>
                          <button
                            onClick={() => removeFromActionPlan(item.id)}
                            className="text-red-400 hover:text-red-300 transition-all"
                          >
                            <XCircle className="w-5 h-5" />
                          </button>
                        </div>
                      ))}
                    </div>
                    
                    <div className="mt-6 text-center">
                      <p className="text-gray-400 text-sm">
                        ✓ Completed: {actionPlan.filter(item => item.completed).length} / {actionPlan.length}
                      </p>
                    </div>
                  </div>
                )}

                {/* CTA Section */}
                <div className="mt-12 bg-gray-800/50 backdrop-blur-md border border-gray-700/50 rounded-2xl p-8 text-center">
                  <h3 className="text-2xl font-bold text-white mb-4">
                    Need Help Implementing These Quick Wins?
                  </h3>
                  <p className="text-gray-400 mb-6">
                    Our experts can help you prioritize and execute your action plan efficiently
                  </p>
                  <div className="flex gap-4 justify-center flex-wrap">
                    <Button 
                      onClick={() => window.location.href = '/book-consultation'}
                      className="px-8 py-4 text-lg bg-cyan-500 hover:bg-cyan-600"
                    >
                      Book Strategy Call
                    </Button>
                    <Button 
                      onClick={() => setActiveSection('circuit')}
                      className="px-8 py-4 text-lg bg-transparent border-2 border-cyan-500 hover:bg-cyan-500/10"
                    >
                      View Implementation Circuit
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </section>
      )}

      {/* NEW: Benchmark Section */}
      {activeSection === 'benchmark' && (
        <section className="py-20 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12 animate-fade-in-up">
              <h2 className="text-4xl font-bold text-white mb-4">📊 Industry Benchmark</h2>
              <p className="text-xl text-gray-400">
                See how your ISO 42001 readiness compares to industry standards
              </p>
            </div>

            {/* Industry & Company Size Selection */}
            <div className="mb-12 grid md:grid-cols-2 gap-6">
              <div className="bg-gray-800/50 rounded-xl p-6">
                <label className="block text-sm font-medium text-gray-300 mb-3">
                  Your Industry
                </label>
                <select
                  value={userIndustry}
                  onChange={(e) => setUserIndustry(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                >
                  <option value="Financial Services">Financial Services</option>
                  <option value="Healthcare">Healthcare</option>
                  <option value="Technology">Technology</option>
                  <option value="Retail">Retail</option>
                  <option value="Manufacturing">Manufacturing</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="bg-gray-800/50 rounded-xl p-6">
                <label className="block text-sm font-medium text-gray-300 mb-3">
                  Company Size (Employees)
                </label>
                <input
                  type="number"
                  value={companySize}
                  onChange={(e) => setCompanySize(parseInt(e.target.value) || 0)}
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                  placeholder="e.g., 100"
                />
              </div>
            </div>

            {/* Benchmark Comparison */}
            <div className="space-y-8">
              {/* Your Score vs Industry Average */}
              <div className="bg-gray-800/50 rounded-xl p-8">
                <h3 className="text-2xl font-bold text-white mb-6">Your Score vs {userIndustry} Average</h3>
                <div className="space-y-6">
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-300">Your Score</span>
                      <span className="text-2xl font-bold text-cyan-400">{calculateAuditScore()}%</span>
                    </div>
                    <div className="comparison-bar">
                      <div className="comparison-bar-fill" style={{ width: `${calculateAuditScore()}%` }}>
                        <span className="text-white text-sm font-semibold">{calculateAuditScore()}%</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-300">Industry Average ({userIndustry})</span>
                      <span className="text-2xl font-bold text-gray-400">{getIndustryBenchmark(userIndustry).averageScore}%</span>
                    </div>
                    <div className="comparison-bar">
                      <div className="comparison-bar-fill bg-gray-600" style={{ width: `${getIndustryBenchmark(userIndustry).averageScore}%` }}>
                        <span className="text-white text-sm font-semibold">{getIndustryBenchmark(userIndustry).averageScore}%</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-gray-300">Top Performers</span>
                      <span className="text-2xl font-bold text-green-400">{getIndustryBenchmark(userIndustry).topPerformers}%</span>
                    </div>
                    <div className="comparison-bar">
                      <div className="comparison-bar-fill bg-green-600" style={{ width: `${getIndustryBenchmark(userIndustry).topPerformers}%` }}>
                        <span className="text-white text-sm font-semibold">{getIndustryBenchmark(userIndustry).topPerformers}%</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Insights */}
                <div className="mt-8 p-6 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Info className="w-6 h-6 text-cyan-400 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="text-lg font-semibold text-white mb-2">Insight</h4>
                      <p className="text-gray-300">
                        {calculateAuditScore() >= getIndustryBenchmark(userIndustry).averageScore
                          ? `Great job! You're performing above the ${userIndustry} industry average. Keep up the momentum!`
                          : `You're ${getIndustryBenchmark(userIndustry).averageScore - calculateAuditScore()}% below the industry average. Our experts can help you close this gap.`
                        }
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Estimated Timeline */}
              <div className="mb-8 bg-gray-800/50 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Clock className="w-6 h-6 text-cyan-400" />
                  <h3 className="text-xl font-bold text-white">Estimated Timeline</h3>
                </div>
                <div className="text-center py-6">
                  <p className="text-4xl font-bold text-cyan-400 mb-2">
                    {getTimelineEstimate(calculateAuditScore()).estimatedMonths}
                  </p>
                  <p className="text-gray-400">{getTimelineEstimate(calculateAuditScore()).description}</p>
                </div>
                <div className="mt-4">
                  <p className="text-sm font-semibold text-gray-300 mb-2">Key Phases:</p>
                  <ul className="space-y-1">
                    {getTimelineEstimate(calculateAuditScore()).phases.map((phase, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-gray-400">
                        <CheckCircle className="w-4 h-4 text-green-400" />
                        {phase}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* Pricing Packages */}
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-white mb-6 text-center">Service Packages & Pricing</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Gen AI & Agentic AI Strategy Package */}
                  <div className="relative overflow-hidden bg-gradient-to-b from-indigo-500/10 to-transparent border border-indigo-500/30 rounded-2xl p-6 hover:border-indigo-500/50 transition-all group">
                    {/* Trending Badge */}
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 text-white px-4 py-1 rounded-full text-xs font-semibold">
                        Trending
                      </div>
                    </div>

                    <div className="text-center mb-4 mt-2">
                      <div className="mb-3 flex justify-center">
                        <div className="w-12 h-12 bg-indigo-500/20 rounded-full flex items-center justify-center">
                          <Shield className="text-indigo-400 w-6 h-6" />
                        </div>
                      </div>
                      <h4 className="text-lg font-bold text-white mb-2">Gen AI & ISO 42001 Strategy</h4>
                      <p className="text-gray-400 text-sm mb-3">AI governance and management frameworks</p>
                      
                      {/* Limited Time Offer Badge */}
                      <div className="inline-flex items-center gap-2 bg-gradient-to-r from-red-500 to-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold mb-4">
                        <Zap className="w-3 h-3" />
                        Limited Time: 50-70% Off!
                      </div>
                    </div>

                    {/* Pricing Grid */}
                    <div className="space-y-3 mb-4">
                      <div className="bg-gray-900/50 rounded-lg p-3">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-gray-400 text-sm">Hourly</span>
                          <span className="text-white font-semibold text-sm">₹2,000 - ₹4,000</span>
                        </div>
                      </div>
                      <div className="bg-gray-900/50 rounded-lg p-3">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-gray-400 text-sm">Consultation</span>
                          <span className="text-white font-semibold text-sm">₹20,000 - ₹90,000</span>
                        </div>
                      </div>
                      <div className="bg-gray-900/50 rounded-lg p-3">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-gray-400 text-sm">Audit</span>
                          <span className="text-white font-semibold text-sm">₹1,00,000 - ₹2,40,000</span>
                        </div>
                      </div>
                      <div className="bg-gray-900/50 rounded-lg p-3">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-gray-400 text-sm">Training</span>
                          <span className="text-white font-semibold text-sm">₹40,000 - ₹1,50,000</span>
                        </div>
                      </div>
                      <div className="bg-gradient-to-r from-indigo-500/20 to-purple-500/20 border border-indigo-500/30 rounded-lg p-3">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-gray-300 text-sm font-semibold">Implementation</span>
                          <div className="text-right">
                            <div className="text-green-400 font-bold text-sm">₹1,50,000 - ₹1,80,000</div>
                            <div className="text-gray-500 text-xs line-through">₹3,00,000 - ₹6,00,000</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Features */}
                    <div className="mb-4">
                      <p className="text-xs font-semibold text-gray-400 mb-2">Includes:</p>
                      <ul className="space-y-1">
                        <li className="flex items-center gap-2 text-xs text-gray-400">
                          <CheckCircle className="w-3 h-3 text-green-400" />
                          ISO 42001 Compliance
                        </li>
                        <li className="flex items-center gap-2 text-xs text-gray-400">
                          <CheckCircle className="w-3 h-3 text-green-400" />
                          AI Governance Framework
                        </li>
                        <li className="flex items-center gap-2 text-xs text-gray-400">
                          <CheckCircle className="w-3 h-3 text-green-400" />
                          Risk Management
                        </li>
                      </ul>
                    </div>

                    <Button 
                      onClick={() => window.location.href = '/pricing'}
                      className="w-full bg-indigo-500 hover:bg-indigo-600 text-white"
                    >
                      View Full Details
                    </Button>
                  </div>

                  {/* ISO Certification & Audits Package */}
                  <div className="relative overflow-hidden bg-gray-900/50 border border-green-500/30 rounded-2xl p-6 hover:border-green-500/50 transition-all group">
                    <div className="text-center mb-4">
                      <div className="mb-3 flex justify-center">
                        <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                          <Shield className="text-green-400 w-6 h-6" />
                        </div>
                      </div>
                      <h4 className="text-lg font-bold text-white mb-2">ISO Certification & Audits</h4>
                      <p className="text-gray-400 text-sm mb-3">International standards compliance</p>
                      
                      {/* Limited Time Offer Badge */}
                      <div className="inline-flex items-center gap-2 bg-gradient-to-r from-red-500 to-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold mb-4">
                        <Zap className="w-3 h-3" />
                        Limited Time: 50-70% Off!
                      </div>
                    </div>

                    {/* Pricing Grid */}
                    <div className="space-y-3 mb-4">
                      <div className="bg-gray-900/50 rounded-lg p-3">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-gray-400 text-sm">Hourly</span>
                          <span className="text-white font-semibold text-sm">₹1,000 - ₹3,000</span>
                        </div>
                      </div>
                      <div className="bg-gray-900/50 rounded-lg p-3">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-gray-400 text-sm">Consultation</span>
                          <span className="text-white font-semibold text-sm">₹10,000 - ₹45,000</span>
                        </div>
                      </div>
                      <div className="bg-gray-900/50 rounded-lg p-3">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-gray-400 text-sm">Audit</span>
                          <span className="text-white font-semibold text-sm">₹25,000 - ₹90,000</span>
                        </div>
                      </div>
                      <div className="bg-gray-900/50 rounded-lg p-3">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-gray-400 text-sm">Training</span>
                          <span className="text-white font-semibold text-sm">₹15,000 - ₹60,000</span>
                        </div>
                      </div>
                      <div className="bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/30 rounded-lg p-3">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-gray-300 text-sm font-semibold">Implementation</span>
                          <div className="text-right">
                            <div className="text-green-400 font-bold text-sm">₹50,000 - ₹72,000</div>
                            <div className="text-gray-500 text-xs line-through">₹1,00,000 - ₹2,40,000</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Features */}
                    <div className="mb-4">
                      <p className="text-xs font-semibold text-gray-400 mb-2">Includes:</p>
                      <ul className="space-y-1">
                        <li className="flex items-center gap-2 text-xs text-gray-400">
                          <CheckCircle className="w-3 h-3 text-green-400" />
                          ISO 9001, 14001, 45001
                        </li>
                        <li className="flex items-center gap-2 text-xs text-gray-400">
                          <CheckCircle className="w-3 h-3 text-green-400" />
                          Compliance Assessment
                        </li>
                        <li className="flex items-center gap-2 text-xs text-gray-400">
                          <CheckCircle className="w-3 h-3 text-green-400" />
                          Documentation Support
                        </li>
                      </ul>
                    </div>

                    <Button 
                      onClick={() => window.location.href = '/pricing'}
                      className="w-full bg-green-500 hover:bg-green-600 text-white"
                    >
                      View Full Details
                    </Button>
                  </div>
                </div>

                {/* View All Pricing CTA */}
                <div className="mt-6 text-center">
                  <button
                    onClick={() => window.location.href = '/pricing'}
                    className="text-cyan-400 hover:text-cyan-300 font-semibold text-sm flex items-center gap-2 mx-auto"
                  >
                    View Complete Pricing Page
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* CTA */}
              <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 rounded-2xl p-8 text-center">
                <h3 className="text-2xl font-bold text-white mb-3">
                  Ready to Close the Gap?
                </h3>
                <p className="text-gray-400 mb-6">
                  Get a customized roadmap based on your industry and company size
                </p>
                <Button 
                  onClick={() => window.location.href = '/book-consultation'}
                  className="px-8 py-4 bg-cyan-500 hover:bg-cyan-600"
                >
                  Book Free Consultation
                </Button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* NEW: Learn / Educational Content Section */}
      {activeSection === 'learn' && (
        <section className="py-20 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12 animate-fade-in-up">
              <h2 className="text-4xl font-bold text-white mb-4">📚 Learn About ISO 42001</h2>
              <p className="text-xl text-gray-400">
                Everything you need to know about AI Management Systems
              </p>
            </div>

            {/* What is ISO 42001 */}
            <div className="mb-12 educational-card animate-fade-in-up stagger-1">
              <h3 className="text-3xl font-bold text-white mb-4">{educationalContent.whatIsISO42001.title}</h3>
              <p className="text-lg text-gray-300 mb-6">{educationalContent.whatIsISO42001.description}</p>
              
              {educationalContent.whatIsISO42001.sections.map((section, idx) => (
                <div key={idx} className="mb-6">
                  <h4 className="text-xl font-semibold text-cyan-400 mb-2">{section.heading}</h4>
                  {section.content && <p className="text-gray-300 mb-3">{section.content}</p>}
                  {section.points && (
                    <ul className="space-y-2 ml-4">
                      {section.points.map((point, pidx) => (
                        <li key={pidx} className="flex items-center gap-2 text-gray-300">
                          <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                          {point}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>

            {/* Framework Comparison */}
            <div className="mb-12 animate-fade-in-up stagger-2">
              <h3 className="text-3xl font-bold text-white mb-6">{educationalContent.frameworkComparison.title}</h3>
              <div className="grid md:grid-cols-2 gap-6">
                {educationalContent.frameworkComparison.frameworks.map((framework, idx) => (
                  <div key={idx} className="educational-card hero-card-hover">
                    <div className="mb-4">
                      <h4 className="text-2xl font-bold text-cyan-400 mb-2">{framework.name}</h4>
                      <p className="text-sm text-gray-400 mb-1">{framework.type}</p>
                      <p className="text-gray-300">{framework.scope}</p>
                    </div>
                    
                    <div className="mb-4 p-3 bg-gray-900/50 rounded-lg">
                      <p className="text-sm text-gray-400 mb-1">Certification:</p>
                      <p className="text-white font-semibold">{framework.certification}</p>
                    </div>

                    <div className="mb-4">
                      <p className="text-sm font-semibold text-gray-300 mb-2">Key Strengths:</p>
                      <ul className="space-y-1">
                        {framework.strengths.map((strength, sidx) => (
                          <li key={sidx} className="flex items-start gap-2 text-sm text-gray-400">
                            <Star className="w-4 h-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                            {strength}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="pt-4 border-t border-gray-700">
                      <p className="text-sm text-gray-400 mb-1">Best For:</p>
                      <p className="text-sm text-cyan-400">{framework.bestFor}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Common Misconceptions */}
            <div className="mb-12 animate-fade-in-up stagger-3">
              <h3 className="text-3xl font-bold text-white mb-6">💡 Common Misconceptions</h3>
              <div className="space-y-4">
                {educationalContent.commonMisconceptions.map((item, idx) => (
                  <div key={idx} className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 hover:border-cyan-500/30 transition-all">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-10 h-10 bg-red-500/20 rounded-full flex items-center justify-center">
                          <XCircle className="w-6 h-6 text-red-400" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <p className="text-lg font-semibold text-red-400 mb-2">Myth: {item.myth}</p>
                        <div className="flex items-start gap-4">
                          <div className="flex-shrink-0">
                            <div className="w-10 h-10 bg-green-500/20 rounded-full flex items-center justify-center">
                              <CheckCircle className="w-6 h-6 text-green-400" />
                            </div>
                          </div>
                          <div>
                            <p className="text-lg font-semibold text-green-400 mb-1">Reality:</p>
                            <p className="text-gray-300">{item.reality}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Downloadable Resources */}
            <div className="animate-fade-in-up stagger-4">
              <h3 className="text-3xl font-bold text-white mb-6">📥 Downloadable Resources</h3>
              <div className="grid md:grid-cols-2 gap-6">
                {educationalContent.downloadableResources.map((resource, idx) => (
                  <div key={idx} className="educational-card hero-card-hover cursor-pointer">
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-cyan-500/20 rounded-lg flex items-center justify-center">
                          <FileText className="w-6 h-6 text-cyan-400" />
                        </div>
                      </div>
                      <div className="flex-1">
                        <h4 className="text-xl font-bold text-white mb-2">{resource.title}</h4>
                        <p className="text-gray-400 text-sm mb-3">{resource.description}</p>
                        <div className="flex items-center gap-2">
                          <span className="px-3 py-1 bg-gray-900/50 rounded-full text-xs text-cyan-400 font-semibold">
                            {resource.type}
                          </span>
                          <Download className="w-4 h-4 text-cyan-400" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 text-center">
                <p className="text-gray-400 text-sm">
                  * Resources are generated based on your assessment responses
                </p>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-12 bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/30 rounded-2xl p-8 text-center">
              <h3 className="text-2xl font-bold text-white mb-3">
                Ready to Begin Your ISO 42001 Journey?
              </h3>
              <p className="text-gray-400 mb-6">
                Start your free assessment or speak with our experts
              </p>
              <div className="flex gap-4 justify-center flex-wrap">
                <Button 
                  onClick={() => setActiveSection('audit')}
                  className="px-8 py-4 bg-cyan-500 hover:bg-cyan-600"
                >
                  Start Assessment
                </Button>
                <Button 
                  onClick={() => window.location.href = '/book-consultation'}
                  className="px-8 py-4 bg-transparent border-2 border-cyan-500 hover:bg-cyan-500/10"
                >
                  Talk to Expert
                </Button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* NEW: AI Recommendations Section */}
      {activeSection === 'ai-recommendations' && (
        <section className="py-20 px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12 animate-fade-in-up">
              <div className="flex items-center justify-center gap-3 mb-4">
                <Star className="w-12 h-12 text-purple-400 animate-pulse" />
                <div className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full text-white text-sm font-bold">
                  AI-Powered
                </div>
              </div>
              <h2 className="text-4xl font-bold text-white mb-4">🤖 AI Recommendations</h2>
              <p className="text-xl text-gray-400">
                Personalized recommendations powered by GPT-5 based on your audit gaps
              </p>
            </div>

            {!aiRecommendations ? (
              /* Generate Recommendations CTA */
              <div className="max-w-2xl mx-auto">
                <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-2xl p-12 text-center">
                  {aiError ? (
                    <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                      <p className="text-red-400">{aiError}</p>
                    </div>
                  ) : null}
                  
                  <div className="mb-6">
                    <div className="w-20 h-20 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Star className="w-10 h-10 text-purple-400" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-3">
                      Generate Your Personalized Recommendations
                    </h3>
                    <p className="text-gray-400 mb-4">
                      Our AI will analyze your assessment responses and generate customized recommendations including:
                    </p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4 mb-8 text-left">
                    <div className="bg-gray-900/50 rounded-lg p-4">
                      <h4 className="text-cyan-400 font-semibold mb-2 flex items-center gap-2">
                        <Zap className="w-4 h-4" />
                        Quick Wins (1-3 months)
                      </h4>
                      <ul className="text-sm text-gray-400 space-y-1">
                        <li>• Immediate improvements</li>
                        <li>• High-impact actions</li>
                        <li>• Detailed steps & resources</li>
                      </ul>
                    </div>
                    <div className="bg-gray-900/50 rounded-lg p-4">
                      <h4 className="text-purple-400 font-semibold mb-2 flex items-center gap-2">
                        <TrendingUp className="w-4 h-4" />
                        Long-term Strategies
                      </h4>
                      <ul className="text-sm text-gray-400 space-y-1">
                        <li>• Comprehensive roadmap</li>
                        <li>• Strategic initiatives</li>
                        <li>• ROI projections</li>
                      </ul>
                    </div>
                  </div>

                  <Button
                    onClick={generateAIRecommendations}
                    disabled={isGeneratingAI || Object.keys(auditResponses).length === 0}
                    className="px-8 py-6 text-lg bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isGeneratingAI ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white inline-block mr-2"></div>
                        Generating Recommendations...
                      </>
                    ) : (
                      <>
                        <Star className="w-5 h-5 inline-block mr-2" />
                        Generate AI Recommendations
                      </>
                    )}
                  </Button>

                  {Object.keys(auditResponses).length === 0 && (
                    <p className="mt-4 text-sm text-yellow-400 flex items-center gap-2 justify-center">
                      <AlertTriangle className="w-4 h-4" />
                      Go to "Deep Dive Audit" tab and answer questions to generate recommendations
                    </p>
                  )}
                  {Object.keys(auditResponses).length > 0 && (
                    <p className="mt-4 text-sm text-cyan-400">
                      {Object.keys(auditResponses).length} questions answered • Ready to generate recommendations
                    </p>
                  )}
                </div>
              </div>
            ) : (
              /* Display Recommendations */
              <div>
                {/* Format Selector */}
                <div className="mb-8 flex gap-3 justify-center flex-wrap">
                  <button
                    onClick={() => setSelectedFormat('bullets')}
                    className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                      selectedFormat === 'bullets' ? 'bg-purple-500 text-white' : 'bg-gray-800 text-gray-400'
                    }`}
                  >
                    📋 Bullet Points
                  </button>
                  <button
                    onClick={() => setSelectedFormat('paragraphs')}
                    className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                      selectedFormat === 'paragraphs' ? 'bg-purple-500 text-white' : 'bg-gray-800 text-gray-400'
                    }`}
                  >
                    📝 Detailed
                  </button>
                  <button
                    onClick={() => setSelectedFormat('checklist')}
                    className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                      selectedFormat === 'checklist' ? 'bg-purple-500 text-white' : 'bg-gray-800 text-gray-400'
                    }`}
                  >
                    ✅ Checklist
                  </button>
                  <button
                    onClick={() => setSelectedFormat('video')}
                    className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                      selectedFormat === 'video' ? 'bg-purple-500 text-white' : 'bg-gray-800 text-gray-400'
                    }`}
                  >
                    🎬 Video Scripts
                  </button>
                </div>

                {/* Executive Summary */}
                <div className="mb-12 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-2xl p-8">
                  <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                    <FileText className="w-6 h-6 text-purple-400" />
                    Executive Summary
                  </h3>
                  <p className="text-gray-300 text-lg mb-6">{aiRecommendations.executive_summary.overview}</p>
                  
                  <div className="grid md:grid-cols-2 gap-4 mb-4">
                    <div className="bg-gray-900/50 rounded-lg p-4">
                      <p className="text-sm text-gray-400 mb-1">Timeline</p>
                      <p className="text-xl font-bold text-cyan-400">{aiRecommendations.executive_summary.estimated_timeline}</p>
                    </div>
                    <div className="bg-gray-900/50 rounded-lg p-4">
                      <p className="text-sm text-gray-400 mb-1">Expected Outcome</p>
                      <p className="text-xl font-bold text-purple-400">{aiRecommendations.executive_summary.expected_outcome}</p>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-semibold text-gray-300 mb-2">Key Priorities:</p>
                    <ul className="space-y-2">
                      {aiRecommendations.executive_summary.key_priorities.map((priority, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-gray-300">
                          <Star className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                          {priority}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Quick Wins */}
                <div className="mb-12">
                  <h3 className="text-3xl font-bold text-white mb-6 flex items-center gap-2">
                    <Zap className="w-8 h-8 text-yellow-400" />
                    Quick Wins (1-3 Months)
                  </h3>
                  <div className="space-y-6">
                    {aiRecommendations.quick_wins.map((qw, idx) => (
                      <div key={idx} className="bg-gray-800/50 rounded-xl p-6 border border-gray-700 hover:border-cyan-500/30 transition-all">
                        <div className="flex items-start gap-4 mb-4">
                          <div className="flex-shrink-0">
                            <div className={`px-3 py-1 rounded-full text-xs font-bold ${
                              qw.priority === 'Critical' ? 'bg-red-500/20 text-red-400' :
                              qw.priority === 'High' ? 'bg-orange-500/20 text-orange-400' :
                              'bg-yellow-500/20 text-yellow-400'
                            }`}>
                              {qw.priority}
                            </div>
                          </div>
                          <div className="flex-1">
                            <h4 className="text-xl font-bold text-white mb-2">{qw.title}</h4>
                            <div className="flex gap-4 text-sm text-gray-400 mb-3">
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {qw.timeline}
                              </span>
                              <span className="flex items-center gap-1">
                                <TrendingUp className="w-4 h-4" />
                                {qw.impact} Impact
                              </span>
                            </div>

                            {selectedFormat === 'bullets' && (
                              <div>
                                <ul className="space-y-2 mb-3">
                                  {qw.action_steps.map((step, sidx) => (
                                    <li key={sidx} className="flex items-start gap-2 text-gray-300">
                                      <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-1" />
                                      {step}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {selectedFormat === 'paragraphs' && (
                              <p className="text-gray-300 mb-4">{qw.description}</p>
                            )}

                            {selectedFormat === 'checklist' && (
                              <div className="space-y-2">
                                <p className="text-sm font-semibold text-gray-400">Action Steps:</p>
                                {qw.action_steps.map((step, sidx) => (
                                  <label key={sidx} className="flex items-center gap-2 text-gray-300 cursor-pointer hover:text-white">
                                    <input type="checkbox" className="w-4 h-4 rounded border-gray-600" />
                                    {step}
                                  </label>
                                ))}
                                <div className="mt-3">
                                  <p className="text-sm font-semibold text-gray-400 mb-1">Resources Needed:</p>
                                  <p className="text-sm text-gray-400">{qw.resources_needed.join(', ')}</p>
                                </div>
                              </div>
                            )}

                            {selectedFormat === 'video' && (
                              <div className="bg-gray-900/50 rounded-lg p-4">
                                <p className="text-xs text-gray-500 mb-2">📹 30-Second Video Script:</p>
                                <p className="text-gray-300 italic">{qw.video_script}</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Long-term Strategies */}
                <div className="mb-12">
                  <h3 className="text-3xl font-bold text-white mb-6 flex items-center gap-2">
                    <TrendingUp className="w-8 h-8 text-purple-400" />
                    Long-term Strategies (6-12 Months)
                  </h3>
                  <div className="space-y-6">
                    {aiRecommendations.long_term_strategies.map((strategy, idx) => (
                      <div key={idx} className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-6">
                        <div className="mb-4">
                          <h4 className="text-2xl font-bold text-white mb-2">{strategy.title}</h4>
                          <div className="flex gap-4 text-sm mb-3">
                            <span className="px-3 py-1 bg-purple-500/20 rounded-full text-purple-400">
                              {strategy.timeline}
                            </span>
                            <span className="px-3 py-1 bg-green-500/20 rounded-full text-green-400">
                              {strategy.strategic_importance} Priority
                            </span>
                          </div>
                        </div>

                        {selectedFormat === 'paragraphs' && (
                          <p className="text-gray-300 mb-4">{strategy.description}</p>
                        )}

                        {selectedFormat === 'bullets' && strategy.implementation_phases && (
                          <div className="mb-4">
                            <p className="text-sm font-semibold text-gray-400 mb-2">Implementation Phases:</p>
                            {strategy.implementation_phases.map((phase, pidx) => (
                              <div key={pidx} className="mb-3 pl-4 border-l-2 border-purple-500/50">
                                <p className="font-semibold text-white">{phase.phase} ({phase.duration})</p>
                                <ul className="mt-1 space-y-1">
                                  {phase.activities.map((activity, aidx) => (
                                    <li key={aidx} className="text-sm text-gray-400">• {activity}</li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                          </div>
                        )}

                        {selectedFormat === 'video' && (
                          <div className="bg-gray-900/50 rounded-lg p-4 mb-4">
                            <p className="text-xs text-gray-500 mb-2">📹 60-Second Video Script:</p>
                            <p className="text-gray-300 italic">{strategy.video_script}</p>
                          </div>
                        )}

                        <div className="bg-gray-900/50 rounded-lg p-3 max-w-md">
                          <p className="text-xs text-gray-400 mb-1">ROI Potential</p>
                          <p className="text-sm font-semibold text-purple-400">{strategy.roi_potential}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-4 justify-center flex-wrap">
                  <Button 
                    onClick={() => {
                      setAiRecommendations(null);
                      localStorage.removeItem('iso42001_ai_recommendations');
                    }}
                    className="px-8 py-4 bg-gray-700 hover:bg-gray-600"
                  >
                    Generate New Recommendations
                  </Button>
                  <Button 
                    onClick={() => window.location.href = '/book-consultation'}
                    className="px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                  >
                    Discuss with Expert
                  </Button>
                </div>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
};

export default ISO42001Tools;
